import {
  faBan,
  faCheckCircle,
  faExchangeAlt,
  faExclamationTriangle,
  faExternalLinkAlt,
  faFileInvoiceDollar,
  faHistory,
  faLink,
  faPlug,
  faPlusCircle,
  faRedo,
  faSearch,
  faStickyNote,
  faSyncAlt,
  faTimesCircle,
  faUnlink,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row, UncontrolledTooltip } from 'reactstrap';
import { useAuth0 } from '../../../../../auth0/reactAuth0Spa';
import { checkLoginForError, formatBearerToken } from '../../../../../Common.functions';
import { deleteData, fetchDataAgnostic, postDataAgnostic } from '../../../../../Services/dataApi';
import DepartmentCreationAndEdit from '../../../../CreateNewEntities/Organizations/Departments/DepartmentCreationAndEdit';
import DepartmentList from '../../../../CreateNewEntities/Organizations/Departments/DepartmentList';
import OrganizationList from '../../../../CreateNewEntities/Organizations/OrganizationList';
import EntityStyle from '../../../../CreateNewEntities/EntityWrapper.style';
import style from './FinancialIntelligencePanel.style';

const fallbackOverview = {
  sync: {
    status: 'notConfigured',
    lastInvoiceSyncAt: null,
    lastPaymentSyncAt: null,
  },
  quickBooks: {
    integrationModel: 'rezrateOwned',
    schemaReady: false,
    productionEnabled: false,
    uiEnabled: false,
    scope: 'com.intuit.quickbooks.accounting',
    stateSecretName: 'quickbooksOAuthStateSecret',
    requiredSecrets: [
      'quickbooksClientID',
      'quickbooksClientSecret',
      'quickbooksOAuthStateSecret',
    ],
    environments: {
      sandbox: {
        label: 'Sandbox',
        baseUrl: 'https://sandbox-quickbooks.api.intuit.com',
        redirectUri: 'https://devapi.rezrate.com/financial-intelligence/quickbooks/oauth/callback',
        localRedirectUri: 'http://localhost:3001/financial-intelligence/quickbooks/oauth/callback',
      },
      production: {
        label: 'Production',
        baseUrl: 'https://quickbooks.api.intuit.com',
        redirectUri: 'https://prodapi.rezrate.com/financial-intelligence/quickbooks/oauth/callback',
      },
    },
    connection: {
      environment: null,
      realmId: null,
      status: 'schemaPending',
      tokenHealth: 'notConfigured',
      lastInvoiceSyncAt: null,
      lastPaymentSyncAt: null,
      lastSuccessfulSyncAt: null,
      lastSyncError: null,
    },
    actions: {
      canConnect: false,
      canDisconnect: false,
      canImportInvoices: false,
      canUseProduction: false,
      canCreateDraftInvoices: false,
      canCloseOutInvoices: false,
    },
  },
  invoiceColumns: [
    { key: 'InvoiceNumber', label: 'Invoice #' },
    { key: 'IssueDate', label: 'Issue Date' },
    { key: 'DueDate', label: 'Due Date' },
    { key: 'Status', label: 'Status' },
    { key: 'CloseOutStatusLabel', label: 'Close-out' },
    { key: 'PaymentTerms', label: 'Terms' },
    { key: 'PurchaseOrderNumber', label: 'PO #' },
    { key: 'BalanceAmount', label: 'Balance' },
  ],
  customerColumns: [
    { key: 'QuickBooksCustomerName', label: 'QuickBooks Customer' },
    { key: 'MappingStatus', label: 'Status' },
    { key: 'MappingReviewStatus', label: 'Review' },
    { key: 'MappingConfidence', label: 'Confidence' },
    { key: 'OrganizationName', label: 'RezRATE Organization' },
    { key: 'DepartmentName', label: 'Program' },
  ],
  reviewQueueColumns: [
    { key: 'QueueType', label: 'Type' },
    { key: 'Severity', label: 'Severity' },
    { key: 'Status', label: 'Status' },
    { key: 'OrganizationName', label: 'Organization' },
    { key: 'DepartmentName', label: 'Department' },
    { key: 'InvoiceNumber', label: 'Invoice #' },
  ],
  lineItemColumns: [
    { key: 'ProductSku', label: 'Product / SKU' },
    { key: 'Description', label: 'Description' },
    { key: 'UnitPrice', label: 'Unit Price' },
    { key: 'Quantity', label: 'Qty' },
    { key: 'BillingPeriodStart', label: 'Period Start' },
    { key: 'BillingPeriodEnd', label: 'Period End' },
    { key: 'TaxApplicable', label: 'Tax' },
  ],
  seasonCoverageColumns: [
    { key: 'SeasonLabel', label: 'Season' },
    { key: 'FinancialStatus', label: 'Financial Status' },
    { key: 'isStatusOverridden', label: 'Overridden' },
    { key: 'CoveredInvoiceCount', label: 'Invoices' },
    { key: 'CoveredLineItemCount', label: 'Line Items' },
  ],
  lineItemSeasonLinkColumns: [
    { key: 'InvoiceNumber', label: 'Invoice #' },
    { key: 'ProductSku', label: 'Product / SKU' },
    { key: 'DepartmentName', label: 'Department' },
    { key: 'SeasonLabel', label: 'Season' },
    { key: 'AllocationAmount', label: 'Allocation' },
    { key: 'AllocationType', label: 'Allocation Type' },
    { key: 'CoverageStatus', label: 'Coverage Status' },
  ],
  paymentColumns: [
    { key: 'InvoiceNumber', label: 'Invoice #' },
    { key: 'QuickBooksCustomerName', label: 'Customer' },
    { key: 'PaymentDate', label: 'Payment Date' },
    { key: 'Amount', label: 'Amount' },
    { key: 'UnappliedAmount', label: 'Unapplied' },
    { key: 'PaymentMethod', label: 'Method' },
    { key: 'ReferenceNumber', label: 'Reference #' },
  ],
  taxExemptionColumns: [
    { key: 'OrganizationName', label: 'Organization' },
    { key: 'DepartmentName', label: 'Program' },
    { key: 'DocumentName', label: 'Document' },
    { key: 'UploadedBy', label: 'Submitter' },
    { key: 'UploadedAt', label: 'Submitted' },
    { key: 'ExemptionType', label: 'Type' },
    { key: 'IssuingState', label: 'State' },
    { key: 'EffectiveDate', label: 'Effective' },
    { key: 'ExpirationDate', label: 'Expires' },
    { key: 'ReviewStatus', label: 'Review Status' },
    { key: 'UploadNotes', label: 'Upload Notes' },
  ],
  billingWorkflowTemplates: [
    {
      key: 'newClientMultiDepartment',
      label: 'New Client Package',
      icon: faUserPlus,
    },
    {
      key: 'existingCustomerRenewal',
      label: 'Renew Customer',
      icon: faRedo,
    },
    {
      key: 'invoiceFirstMapping',
      label: 'Map Imported Invoice',
      icon: faExchangeAlt,
    },
  ],
  databaseChangeTicket: {
    key: 'REZ-1004',
    requiredBeforeWriteWorkflows: true,
  },
  reporting: {
    summary: {
      totalAllocatedRevenue: 0,
      totalPaidRevenue: 0,
      totalOutstandingBalance: 0,
      prepaidFutureCoverage: 0,
    },
    revenueByInvoiceYear: [],
    revenueByPaymentYear: [],
    revenueBySeasonYear: [],
    revenueByOrganization: [],
    revenueByDepartment: [],
    customerRevenueMix: [],
    prepaidFutureCoverage: [],
    ltvByOrganization: [],
    ltvByDepartment: [],
    taxExemptionStatusSummary: [],
  },
  invoices: [],
  lineItems: [],
  payments: [],
  customers: [],
  reviewQueue: [],
  lineItemSeasonLinks: [],
  seasonStatuses: [],
  taxExemptionDocuments: [],
};

// REZ-988: mirrors the seasonFinancialStatuses Set in
// routes/financialIntelligence.js so the override prompt only ever offers
// recognized statuses.
const SEASON_FINANCIAL_STATUSES = [
  'Paid / Covered',
  'Partially Paid',
  'Invoice Sent / Pending Payment',
  'Courtesy Generated / Awaiting Payment',
  'Prepaid / Covered by Prepayment',
  'Comped',
  'Discounted',
  'Overdue',
  'Canceled / Voided',
  'Not Invoiced',
  'Needs Review',
];

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }

  return value;
};

const formatCellValue = (key, value) => {
  if (key === 'isStatusOverridden') {
    return value ? 'Overridden' : 'Computed';
  }

  if (key === 'AllocationType') {
    const normalized = normalizeSearchText(value);
    if (normalized === 'amount') return 'Standard';
    if (normalized === 'courtesy') return 'Courtesy';
    if (normalized === 'prepayment') return 'Prepayment';
    if (normalized === 'comped') return 'Comped';
    if (normalized === 'discount') return 'Discount';
  }

  if (
    [
      'allocatedRevenue',
      'paidRevenue',
      'outstandingBalance',
      'prepaidAllocationAmount',
      'totalAllocatedRevenue',
      'totalPaidRevenue',
      'totalOutstandingBalance',
      'prepaidFutureCoverage',
    ].includes(key)
  ) {
    return formatMoney(value);
  }

  if (['invoiceYear', 'paymentYear', 'seasonYear', 'count', 'allocationCount', 'coveredInvoiceCount'].includes(key)) {
    return formatValue(value);
  }

  return formatValue(value);
};

const normalizeSearchText = (value) => `${value || ''}`.toLowerCase();

const getRowKey = (row, rowIndex) =>
  row.pk_FinancialRecord ||
  row.pk_QuickBooksInvoice ||
  row.pk_QuickBooksInvoiceLineItem ||
  row.pk_QuickBooksCustomerMapping ||
  row.pk_FinancialReviewQueueItem ||
  row.id ||
  rowIndex;

const rowContainsSearch = (row, searchText) => {
  if (!searchText) {
    return true;
  }

  return Object.values(row || {}).some((value) => normalizeSearchText(value).includes(searchText));
};

const formatMoney = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '--';
  }

  return `$${numericValue.toFixed(2)}`;
};

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

const formatDateLabel = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) {
    return '--';
  }

  return parsed.toISOString().slice(0, 10);
};

const isInvoiceOverdue = (invoice) => {
  const dueDate = parseDateValue(invoice && invoice.DueDate);
  const balanceDue = Number(invoice && invoice.BalanceDue);
  const normalizedStatus = normalizeSearchText(invoice && invoice.Status);

  if (!dueDate || !Number.isFinite(balanceDue) || balanceDue <= 0) {
    return false;
  }

  if (['paid', 'void', 'voided'].includes(normalizedStatus)) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);

  return dueDate < today;
};

const getQuickBooksInvoiceUrl = (invoice) => {
  if (!invoice) {
    return null;
  }

  if (invoice.QuickBooksViewUrl) {
    return invoice.QuickBooksViewUrl;
  }

  if (!invoice.QuickBooksInvoiceId) {
    return null;
  }

  return `https://app.qbo.intuit.com/app/invoice?txnId=${encodeURIComponent(invoice.QuickBooksInvoiceId)}`;
};

const getQuickBooksPayUrl = (invoice) => {
  if (!invoice || !invoice.QuickBooksPayUrl) {
    return null;
  }

  return invoice.QuickBooksPayUrl;
};

const getDraftNotSentStatus = (invoice) => {
  const normalizedStatus = normalizeSearchText(invoice && invoice.Status);
  return normalizedStatus.includes('draft') || normalizedStatus.includes('pending');
};

const getInvoiceCloseOutState = (invoice) => {
  if (!invoice) {
    return { status: 'unavailable', label: '--' };
  }

  if (invoice.CloseOutStatus || invoice.CloseOutStatusLabel) {
    return {
      status: invoice.CloseOutStatus || normalizeSearchText(invoice.CloseOutStatusLabel),
      label: invoice.CloseOutStatusLabel || invoice.CloseOutStatus,
    };
  }

  const normalizedStatus = normalizeSearchText(invoice.Status);
  if (invoice.IsVoided || normalizedStatus === 'voided') {
    return { status: 'confirmed', label: 'Voided' };
  }
  if (normalizedStatus === 'paid' || normalizedStatus === 'partiallypaid') {
    return { status: 'manual_only', label: 'Manual in QuickBooks' };
  }
  return { status: 'available', label: 'Available' };
};

const getInvoiceCloseOutDisabledReason = (invoice) => {
  if (!invoice) {
    return 'Select an invoice before running a close-out.';
  }

  const state = getInvoiceCloseOutState(invoice);
  if (state.status === 'manual_only') {
    return invoice.CloseOutManualReason || 'Paid or partially paid invoices must be voided manually in QuickBooks.';
  }
  if (state.status === 'confirmed') {
    return 'This invoice is already voided.';
  }
  if (state.status === 'pending') {
    return 'A QuickBooks void has already been requested and is waiting for sync confirmation.';
  }
  return '';
};

const getDerivedPastDueLabel = (invoice) => {
  if (isInvoiceOverdue(invoice)) {
    return 'Past Due';
  }

  const normalizedStatus = normalizeSearchText(invoice && invoice.Status);
  if (normalizedStatus === 'paid') {
    return 'Paid';
  }

  if (normalizedStatus.includes('partial')) {
    return 'Partially Paid';
  }

  if (Number(invoice && invoice.BalanceDue) > 0) {
    return 'Open';
  }

  return formatValue(invoice && invoice.Status);
};

const getPaymentEventType = (payment) => {
  const memo = normalizeSearchText(payment && payment.Memo);
  const amount = Number(payment && payment.Amount);

  if (memo.includes('refund') || amount < 0) {
    return 'Refund';
  }

  if (memo.includes('credit')) {
    return 'Credit';
  }

  if (memo.includes('adjust')) {
    return 'Adjustment';
  }

  if (payment && payment.IsVoided) {
    return 'Voided payment';
  }

  return 'Payment';
};

const getTaxExemptionDerivedStatus = (document) => {
  const normalizedStatus = normalizeSearchText(document && document.ReviewStatus);
  const expirationDate = parseDateValue(document && document.ExpirationDate);

  if (normalizedStatus === 'approved' && expirationDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expirationDate.setHours(0, 0, 0, 0);

    if (expirationDate < today) {
      return 'expired';
    }
  }

  return normalizedStatus || 'not_submitted';
};

const getTaxExemptionStatusLabel = (document) => {
  const status = getTaxExemptionDerivedStatus(document);

  if (status === 'submitted_pending') return 'Submitted / Pending Review';
  if (status === 'needs_update') return 'Needs Update';
  if (status === 'not_submitted') return 'Not Submitted';
  if (status === 'not_applicable') return 'Not Applicable';
  if (status === 'approved') return 'Approved';
  if (status === 'rejected') return 'Rejected';
  if (status === 'expired') return 'Expired';

  return formatValue(document && document.ReviewStatus);
};

const getReviewItemAgeDays = (item) => {
  const createdAt = parseDateValue(item && item.createdAt);
  if (!createdAt) {
    return null;
  }

  const now = new Date();
  return Math.max(0, Math.floor((now.getTime() - createdAt.getTime()) / (24 * 60 * 60 * 1000)));
};

const reviewAgeMatches = (item, filterValue) => {
  if (filterValue === 'all') {
    return true;
  }

  const ageDays = getReviewItemAgeDays(item);
  if (ageDays === null) {
    return false;
  }

  if (filterValue === '0to7') {
    return ageDays <= 7;
  }

  if (filterValue === '8to30') {
    return ageDays >= 8 && ageDays <= 30;
  }

  if (filterValue === '31plus') {
    return ageDays >= 31;
  }

  return true;
};

const renderTable = ({ columns, rows, emptyText, onRowClick, getRowStyle, renderActions, actionsLabel }) => {
  return (
    <div style={style.tableWrap}>
      <table style={style.table}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} style={style.th}>
                {column.label}
              </th>
            ))}
            {renderActions ? <th style={style.th}>{actionsLabel || 'Actions'}</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map((row, rowIndex) => (
              <tr
                key={getRowKey(row, rowIndex)}
                style={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  ...(getRowStyle ? getRowStyle(row) : {}),
                }}
                onClick={() => {
                  if (onRowClick) {
                    onRowClick(row);
                  }
                }}
              >
                {columns.map((column) => (
                  <td key={column.key} style={style.td}>
                    {formatCellValue(column.key, row[column.key])}
                  </td>
                ))}
                {renderActions ? (
                  <td style={style.td} onClick={(e) => e.stopPropagation()}>
                    {renderActions(row)}
                  </td>
                ) : null}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length + (renderActions ? 1 : 0)}>
                <div style={style.emptyState}>{emptyText}</div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const FinancialIntelligencePanel = ({ getDepartments, quickBooksUiEnabled: quickBooksUiEnabledOverride = false }) => {
  const ORGANIZATION_TABS = {
    departments: 'departments',
    billing: 'billing',
  };
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [departmentLogo, setDepartmentLogo] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [departmentReplyToAddress, setDepartmentReplyToAddress] = useState('');
  const [departmentReplyToName, setDepartmentReplyToName] = useState('');
  const [departmentShortName, setDepartmentShortName] = useState('');
  const [departmentWebsite, setDepartmentWebsite] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isCreatingDepartment, setIsCreatingDepartment] = useState(false);
  const [activeOrganizationTab, setActiveOrganizationTab] = useState(ORGANIZATION_TABS.departments);
  const [overview, setOverview] = useState(fallbackOverview);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerFilter, setCustomerFilter] = useState('unmapped');
  const [reviewFilter, setReviewFilter] = useState('open');
  const [reviewTypeFilter, setReviewTypeFilter] = useState('all');
  const [reviewAgeFilter, setReviewAgeFilter] = useState('all');
  const [followUpFilter, setFollowUpFilter] = useState('all');
  const [taxExemptionFilter, setTaxExemptionFilter] = useState('all');
  const [isLoadingFinancials, setIsLoadingFinancials] = useState(false);
  const [loadState, setLoadState] = useState('fallback');
  const [quickBooksActionMessage, setQuickBooksActionMessage] = useState('');
  const [isQuickBooksActionLoading, setIsQuickBooksActionLoading] = useState(false);
  const [customerMappingLoadingMessage, setCustomerMappingLoadingMessage] = useState('');
  const [isReviewQueueLoading, setIsReviewQueueLoading] = useState(false);
  const [draftIssueDate, setDraftIssueDate] = useState('');
  const [draftDueDate, setDraftDueDate] = useState('');
  const [closeOutReason, setCloseOutReason] = useState('');
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [allocationEditor, setAllocationEditor] = useState({
    lineItem: null,
    allocations: [],
    totals: null,
  });
  const [invoiceWorkspace, setInvoiceWorkspace] = useState({
    invoice: null,
    lineItems: [],
    warnings: [],
    guidance: null,
    mappingTargets: { departments: [], seasons: [] },
  });
  const [allocationMessage, setAllocationMessage] = useState('');
  const [isAllocationLoading, setIsAllocationLoading] = useState(false);
  const [isAllocationSaving, setIsAllocationSaving] = useState(false);
  const [isWorkspaceLoading, setIsWorkspaceLoading] = useState(false);
  const [isWorkspaceActionLoading, setIsWorkspaceActionLoading] = useState(false);
  const [selectedTaxExemptionDocument, setSelectedTaxExemptionDocument] = useState(null);
  const [taxExemptionAudit, setTaxExemptionAudit] = useState([]);
  const [mappingAuditFor, setMappingAuditFor] = useState(null);
  const [mappingAuditRows, setMappingAuditRows] = useState([]);
  const [allocationAuditFor, setAllocationAuditFor] = useState(null);
  const [allocationAuditRows, setAllocationAuditRows] = useState([]);
  const [seasonOverrideMessage, setSeasonOverrideMessage] = useState('');
  const [isSeasonOverrideLoading, setIsSeasonOverrideLoading] = useState(false);
  const [seasonOverrideHistoryFor, setSeasonOverrideHistoryFor] = useState(null);
  const [seasonOverrideHistoryRows, setSeasonOverrideHistoryRows] = useState([]);
  const [taxExemptionActionMessage, setTaxExemptionActionMessage] = useState('');
  const [isTaxExemptionActionLoading, setIsTaxExemptionActionLoading] = useState(false);
  const [taxExemptionReviewDraft, setTaxExemptionReviewDraft] = useState({
    ReviewStatus: 'submitted_pending',
    EffectiveDate: '',
    ExpirationDate: '',
    ReviewNotes: '',
  });

  const { getTokenSilently, loginWithRedirect } = useAuth0();

  const loadOverview = useCallback(() => {
    let isMounted = true;
    setIsLoadingFinancials(true);

    getTokenSilently()
      .then((token) => fetchDataAgnostic('financial-intelligence/admin/overview', {}, formatBearerToken(token)))
      .then((res) => {
        if (!isMounted) {
          return;
        }

        setOverview({ ...fallbackOverview, ...(res.data || {}) });
        setLoadState('api');
        setIsLoadingFinancials(false);
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }

        setOverview(fallbackOverview);
        setLoadState('fallback');
        setIsLoadingFinancials(false);
        checkLoginForError({ err, loginWithRedirect });
      });

    return () => {
      isMounted = false;
    };
  }, [getTokenSilently, loginWithRedirect]);

  useEffect(() => {
    const cleanup = loadOverview();
    return cleanup;
  }, [loadOverview]);

  useEffect(() => {
    setSelectedLineItem(null);
    setAllocationEditor({
      lineItem: null,
      allocations: [],
      totals: null,
    });
    setInvoiceWorkspace({
      invoice: null,
      lineItems: [],
      warnings: [],
      guidance: null,
      mappingTargets: { departments: [], seasons: [] },
    });
    setAllocationMessage('');
    setSelectedTaxExemptionDocument(null);
    setTaxExemptionAudit([]);
    setTaxExemptionActionMessage('');
  }, [selectedInvoice, selectedDepartment, selectedOrganization]);

  useEffect(() => {
    setIsCreatingDepartment(false);
  }, [selectedOrganization]);

  useEffect(() => {
    if (selectedDepartment) {
      setIsCreatingDepartment(false);
    }
  }, [selectedDepartment]);

  useEffect(() => {
    if (!selectedTaxExemptionDocument) {
      setTaxExemptionReviewDraft({
        ReviewStatus: 'submitted_pending',
        EffectiveDate: '',
        ExpirationDate: '',
        ReviewNotes: '',
      });
      return;
    }

    setTaxExemptionReviewDraft({
      ReviewStatus: selectedTaxExemptionDocument.ReviewStatus || 'submitted_pending',
      EffectiveDate: selectedTaxExemptionDocument.EffectiveDate
        ? formatDateLabel(selectedTaxExemptionDocument.EffectiveDate)
        : '',
      ExpirationDate: selectedTaxExemptionDocument.ExpirationDate
        ? formatDateLabel(selectedTaxExemptionDocument.ExpirationDate)
        : '',
      ReviewNotes: selectedTaxExemptionDocument.ReviewNotes || '',
    });
  }, [selectedTaxExemptionDocument]);

  useEffect(() => {
    if (!selectedInvoice) {
      setDraftIssueDate('');
      setDraftDueDate('');
      setCloseOutReason('');
      return;
    }

    setDraftIssueDate(selectedInvoice.IssueDate || '');
    setDraftDueDate(selectedInvoice.DueDate || '');
    setCloseOutReason(selectedInvoice.VoidRequestReason || '');
  }, [selectedInvoice]);

  const updateOrganizationArrayValues = (data) => {
    setOrganizations((prevState) =>
      prevState.map((item) => {
        if (item.pk_Organization === data.pk_Organization) {
          return {
            ...item,
            OrganizationName: data.OrganizationName,
            OrganizationShortName: data.OrganizationShortName,
            OrganizationWebsite: data.OrganizationWebsite,
          };
        }

        return item;
      })
    );
  };

  const renderDepartmentName = () => {
    const selectedOrganizationRecord = organizations.find(
      (organization) => Number(organization.pk_Organization) === Number(selectedOrganization),
    );

    if (activeOrganizationTab === ORGANIZATION_TABS.billing) {
      if (selectedOrganizationRecord && selectedOrganizationRecord.OrganizationName) {
        return `${selectedOrganizationRecord.OrganizationName} Billing`;
      }

      return 'Organization Billing';
    }

    if (selectedDepartment) {
      if (departmentName && departmentName.trim().length > 0) {
        return departmentName;
      }

      return 'Loading...';
    }

    if (isCreatingDepartment) {
      return 'Create New Department';
    }

    if (selectedOrganizationRecord && selectedOrganizationRecord.OrganizationName) {
      return `${selectedOrganizationRecord.OrganizationName} Departments`;
    }

    return 'Create New Department';
  };

  const selectedOrganizationId = selectedOrganization ? Number(selectedOrganization) : null;
  const selectedDepartmentId = selectedDepartment ? Number(selectedDepartment) : null;
  const selectedOrganizationRecord = organizations.find(
    (organization) => Number(organization.pk_Organization) === selectedOrganizationId,
  );
  const invoices = overview.invoices || [];
  const customers = overview.customers || [];
  const payments = overview.payments || [];
  const reviewQueue = overview.reviewQueue || [];
  const taxExemptionDocuments = overview.taxExemptionDocuments || [];
  const reporting = overview.reporting || fallbackOverview.reporting;
  const billingDepartments = overview.departments || [];
  const lineItemSeasonLinks = overview.lineItemSeasonLinks || [];
  const seasonStatuses = overview.seasonStatuses || [];
  const departmentById = departments.reduce((accumulator, department) => {
    accumulator[Number(department.pk_Department)] = department;
    return accumulator;
  }, {});
  const seasonById = departments.reduce((accumulator, department) => {
    (department.Seasons || []).forEach((season) => {
      accumulator[Number(season.pk_Season)] = {
        ...season,
        fk_Department: department.pk_Department,
        DepartmentName: department.DepartmentName,
        OrganizationName: department.OrganizationName,
      };
    });
    return accumulator;
  }, {});
  const invoicesByPk = invoices.reduce((accumulator, invoice) => {
    accumulator[Number(invoice.pk_QuickBooksInvoice)] = invoice;
    return accumulator;
  }, {});

  useEffect(() => {
    if (!selectedInvoice || !selectedInvoice.pk_QuickBooksInvoice) {
      return;
    }

    const refreshedInvoice = invoicesByPk[Number(selectedInvoice.pk_QuickBooksInvoice)];
    if (
      refreshedInvoice &&
      (
        refreshedInvoice.CloseOutStatus !== selectedInvoice.CloseOutStatus ||
        refreshedInvoice.CloseOutStatusLabel !== selectedInvoice.CloseOutStatusLabel ||
        refreshedInvoice.VoidRequestStatus !== selectedInvoice.VoidRequestStatus ||
        refreshedInvoice.VoidFailedAt !== selectedInvoice.VoidFailedAt ||
        refreshedInvoice.VoidFailureReason !== selectedInvoice.VoidFailureReason
      )
    ) {
      setSelectedInvoice((prevState) => ({
        ...(prevState || {}),
        ...refreshedInvoice,
      }));
    }
  }, [invoices, selectedInvoice]);

  const selectedInvoiceLineItems =
    invoiceWorkspace.lineItems && invoiceWorkspace.lineItems.length
      ? invoiceWorkspace.lineItems
      : selectedInvoice && selectedInvoice.lineItems
      ? selectedInvoice.lineItems
      : (overview.lineItems || []).filter(
          (lineItem) => Number(lineItem.fk_QuickBooksInvoice) === Number(selectedInvoice && selectedInvoice.pk_QuickBooksInvoice),
        );

  const baseInvoices = invoices.filter((invoice) => {
    if (selectedDepartmentId) {
      return lineItemSeasonLinks.some(
        (link) =>
          Number(link.fk_QuickBooksInvoice) === Number(invoice.pk_QuickBooksInvoice) &&
          Number(link.fk_Department) === selectedDepartmentId,
      );
    }

    if (selectedOrganizationId) {
      return Number(invoice.fk_Organization) === selectedOrganizationId;
    }

    return true;
  });

  const visibleInvoices = baseInvoices
    .filter((invoice) => rowContainsSearch(invoice, normalizeSearchText(invoiceSearch)))
    .filter((invoice) => {
      if (invoiceFilter === 'all') {
        return true;
      }

      if (invoiceFilter === 'open') {
        return ['open', 'unpaid', 'partially paid', 'partiallypaid', 'overdue'].includes(
          normalizeSearchText(invoice.Status),
        );
      }

      if (invoiceFilter === 'paid') {
        return normalizeSearchText(invoice.Status) === 'paid';
      }

      if (invoiceFilter === 'overdue') {
        return isInvoiceOverdue(invoice);
      }

      if (invoiceFilter === 'needsReview') {
        return invoice.NeedsReview === true || normalizeSearchText(invoice.Status) === 'needs review';
      }

      return true;
    });

  const visibleOverdueInvoices = visibleInvoices.filter((invoice) => isInvoiceOverdue(invoice));

  const visibleSeasonCoverage = seasonStatuses.filter((season) => {
    if (!selectedDepartmentId) {
      return false;
    }

    return Number(season.fk_Department) === selectedDepartmentId;
  });

  const visibleLineItemSeasonLinks = lineItemSeasonLinks.filter((link) => {
    if (selectedInvoice && selectedInvoice.pk_QuickBooksInvoice) {
      return Number(link.fk_QuickBooksInvoice) === Number(selectedInvoice.pk_QuickBooksInvoice);
    }

    if (selectedDepartmentId) {
      return Number(link.fk_Department) === selectedDepartmentId;
    }

    return false;
  });

  const visibleCustomers = customers
    .filter((customer) => {
      if (customerFilter === 'all') {
        return true;
      }

      return normalizeSearchText(customer.MappingStatus) === customerFilter;
    })
    .filter((customer) => rowContainsSearch(customer, normalizeSearchText(customerSearch)));
  const selectedOrganizationMappedCustomer = customers.find((customer) => {
    if (normalizeSearchText(customer.MappingStatus) !== 'mapped' || !selectedOrganizationId) {
      return false;
    }

    if (customer.fk_Organization && Number(customer.fk_Organization) === selectedOrganizationId) {
      return true;
    }

    return (
      selectedOrganizationRecord &&
      normalizeSearchText(customer.OrganizationName) === normalizeSearchText(selectedOrganizationRecord.OrganizationName)
    );
  });

  const visibleReviewQueue = reviewQueue.filter((item) => {
    if (selectedDepartmentId && Number(item.fk_Department) !== selectedDepartmentId) {
      return false;
    }

    if (!selectedDepartmentId && selectedOrganizationId && Number(item.fk_Organization) !== selectedOrganizationId) {
      return false;
    }

    if (reviewFilter !== 'all' && normalizeSearchText(item.Status) !== reviewFilter) {
      return false;
    }

    if (reviewTypeFilter !== 'all' && normalizeSearchText(item.QueueType) !== normalizeSearchText(reviewTypeFilter)) {
      return false;
    }

    return reviewAgeMatches(item, reviewAgeFilter);
  });

  const visiblePayments = payments.filter((payment) => {
    if (selectedInvoice && selectedInvoice.pk_QuickBooksInvoice) {
      return Number(payment.fk_QuickBooksInvoice) === Number(selectedInvoice.pk_QuickBooksInvoice);
    }

    if (selectedDepartmentId) {
      return lineItemSeasonLinks.some(
        (link) =>
          Number(link.fk_QuickBooksInvoice) === Number(payment.fk_QuickBooksInvoice) &&
          Number(link.fk_Department) === selectedDepartmentId,
      );
    }

    if (selectedOrganizationId) {
      return Number(payment.fk_Organization) === selectedOrganizationId;
    }

    return true;
  });

  const visibleTaxExemptionDocuments = taxExemptionDocuments.filter((document) => {
    if (selectedDepartmentId) {
      if (document.fk_Department) {
        return Number(document.fk_Department) === selectedDepartmentId;
      }
      const department = departmentById[selectedDepartmentId];
      return department ? Number(document.fk_Organization) === Number(department.fk_Organization) : true;
    }

    if (selectedOrganizationId) {
      return Number(document.fk_Organization) === selectedOrganizationId;
    }

    return true;
  });

  const filteredTaxExemptionDocuments = visibleTaxExemptionDocuments
    .map((document) => ({
      ...document,
      ReviewStatusDisplay: getTaxExemptionStatusLabel(document),
      ScopeLabel: document.DepartmentName || 'Organization-wide',
    }))
    .filter((document) => {
      const derivedStatus = getTaxExemptionDerivedStatus(document);

      if (taxExemptionFilter === 'all') {
        return true;
      }

      if (taxExemptionFilter === 'attention') {
        return ['submitted_pending', 'needs_update', 'expired', 'rejected'].includes(derivedStatus);
      }

      return derivedStatus === taxExemptionFilter;
    })
    .sort((left, right) => {
      const leftDate = parseDateValue(left.UploadedAt || left.createdAt);
      const rightDate = parseDateValue(right.UploadedAt || right.createdAt);

      if (!leftDate && !rightDate) {
        return 0;
      }

      if (!leftDate) {
        return 1;
      }

      if (!rightDate) {
        return -1;
      }

      return rightDate.getTime() - leftDate.getTime();
    });

  const selectedInvoicePayments = visiblePayments
    .slice()
    .sort((left, right) => {
      const leftDate = parseDateValue(left.PaymentDate || left.updatedAt || left.ImportedAt);
      const rightDate = parseDateValue(right.PaymentDate || right.updatedAt || right.ImportedAt);

      if (!leftDate && !rightDate) {
        return 0;
      }

      if (!leftDate) {
        return 1;
      }

      if (!rightDate) {
        return -1;
      }

      return rightDate.getTime() - leftDate.getTime();
    });

  const billingFollowUps = (() => {
    const followUpItems = [];

    visibleOverdueInvoices.forEach((invoice) => {
      followUpItems.push({
        id: `overdue-${invoice.pk_QuickBooksInvoice}`,
        type: 'overdueInvoice',
        label: 'Overdue invoice',
        organizationName: invoice.OrganizationName,
        departmentName: '--',
        subject: invoice.InvoiceNumber || 'Imported invoice',
        detail: `Due ${formatDateLabel(invoice.DueDate)} with ${formatMoney(invoice.BalanceDue)} still open.`,
        status: invoice.Status || 'Open',
        invoice,
        quickBooksUrl: getQuickBooksInvoiceUrl(invoice),
        payNowUrl: getQuickBooksPayUrl(invoice),
        payNowAvailable: !!getQuickBooksPayUrl(invoice),
      });
    });

    (overview.lineItems || [])
      .filter((lineItem) => {
        if (selectedDepartmentId) {
          return selectedOrganizationId ? Number(lineItem.fk_Organization) === selectedOrganizationId : true;
        }

        if (selectedOrganizationId) {
          return Number(lineItem.fk_Organization) === selectedOrganizationId;
        }

        return true;
      })
      .filter(
        (lineItem) =>
          !lineItemSeasonLinks.some(
            (link) => Number(link.fk_QuickBooksInvoiceLineItem) === Number(lineItem.pk_QuickBooksInvoiceLineItem),
          ),
      )
      .forEach((lineItem) => {
        const invoice = invoicesByPk[Number(lineItem.fk_QuickBooksInvoice)];
        followUpItems.push({
          id: `missing-link-${lineItem.pk_QuickBooksInvoiceLineItem}`,
          type: 'missingSeasonLinkage',
          label: 'Missing season linkage',
          organizationName: lineItem.OrganizationName || (invoice && invoice.OrganizationName) || '--',
          departmentName: '--',
          subject: `${lineItem.InvoiceNumber || 'Invoice'} • ${lineItem.ProductSku || 'Line item'}`,
          detail: 'Imported line item is not linked to any department season yet.',
          status: 'Needs allocation',
          invoice,
          quickBooksUrl: getQuickBooksInvoiceUrl(invoice),
          payNowUrl: getQuickBooksPayUrl(invoice),
          payNowAvailable: false,
          lineItem,
        });
      });

    Object.values(seasonById)
      .filter((season) => Number(season.Active) === 1 || season.Active === true)
      .forEach((season) => {
        const endDate = parseDateValue(season.SeasonEndDate);
        if (!endDate) {
          return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const renewalWindowEnd = new Date(today);
        renewalWindowEnd.setDate(renewalWindowEnd.getDate() + 60);

        endDate.setHours(0, 0, 0, 0);
        if (endDate < today || endDate > renewalWindowEnd) {
          return;
        }

        if (selectedDepartmentId && Number(season.fk_Department) !== selectedDepartmentId) {
          return;
        }

        const department = departmentById[Number(season.fk_Department)];
        if (!selectedDepartmentId && selectedOrganizationId && Number(department && department.fk_Organization) !== selectedOrganizationId) {
          return;
        }

        const hasCoverage = lineItemSeasonLinks.some((link) => Number(link.fk_Season) === Number(season.pk_Season));

        if (!hasCoverage) {
          followUpItems.push({
            id: `renewal-${season.pk_Season}`,
            type: 'renewalUpcoming',
            label: 'Renewal upcoming',
            organizationName: department && department.OrganizationName ? department.OrganizationName : '--',
            departmentName: department && department.DepartmentName ? department.DepartmentName : '--',
            subject: season.SeasonName || 'Active season',
            detail: `Season ends ${formatDateLabel(season.SeasonEndDate)} and has no linked billing coverage yet.`,
            status: 'Upcoming',
            quickBooksUrl: null,
            payNowAvailable: false,
          });
        }
      });

    visibleInvoices.filter((invoice) => getDraftNotSentStatus(invoice)).forEach((invoice) => {
      followUpItems.push({
        id: `draft-${invoice.pk_QuickBooksInvoice}`,
        type: 'draftNotSent',
        label: 'Draft created not sent',
        organizationName: invoice.OrganizationName,
        departmentName: '--',
        subject: invoice.InvoiceNumber || 'Draft invoice',
        detail: 'Best-effort detection: imported invoice still appears to be draft or pending in QuickBooks.',
        status: invoice.Status || 'Draft',
        invoice,
        quickBooksUrl: getQuickBooksInvoiceUrl(invoice),
        payNowUrl: getQuickBooksPayUrl(invoice),
        payNowAvailable: false,
      });
    });

    return followUpItems.filter((item) => {
      if (followUpFilter === 'all') {
        return true;
      }

      return item.type === followUpFilter;
    });
  })();

  const followUpCounts = billingFollowUps.reduce((accumulator, item) => {
    accumulator[item.type] = (accumulator[item.type] || 0) + 1;
    return accumulator;
  }, {});

  const openQuickBooksUrl = (url) => {
    if (!url) {
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const loadTaxExemptionAudit = (documentPk) => {
    if (!documentPk) {
      setTaxExemptionAudit([]);
      return Promise.resolve();
    }

    return getTokenSilently()
      .then((token) =>
        fetchDataAgnostic(
          `financial-intelligence/tax-exemptions/${documentPk}/audit`,
          {},
          formatBearerToken(token),
        ),
      )
      .then((res) => {
        setTaxExemptionAudit(Array.isArray(res.data && res.data.rows) ? res.data.rows : []);
      })
      .catch((err) => {
        setTaxExemptionAudit([]);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const selectTaxExemptionDocument = (document) => {
    setSelectedTaxExemptionDocument(document);
    setTaxExemptionActionMessage('');
    loadTaxExemptionAudit(document && document.pk_TaxExemptionDocument);
  };

  const runTaxExemptionReview = (statusOverride = null) => {
    if (!selectedTaxExemptionDocument || !selectedTaxExemptionDocument.pk_TaxExemptionDocument) {
      return;
    }

    const payload = {
      ReviewStatus: statusOverride || taxExemptionReviewDraft.ReviewStatus || 'submitted_pending',
      ReviewNotes: taxExemptionReviewDraft.ReviewNotes || null,
      EffectiveDate: taxExemptionReviewDraft.EffectiveDate || null,
      ExpirationDate: taxExemptionReviewDraft.ExpirationDate || null,
    };

    setTaxExemptionActionMessage('');
    setIsTaxExemptionActionLoading(true);

    getTokenSilently()
      .then((token) =>
        postDataAgnostic(
          `financial-intelligence/tax-exemptions/${selectedTaxExemptionDocument.pk_TaxExemptionDocument}/review`,
          {},
          payload,
          formatBearerToken(token),
        ),
      )
      .then((res) => {
        const updatedRow = (res.data && res.data.row) || selectedTaxExemptionDocument;
        setSelectedTaxExemptionDocument((prevState) =>
          prevState ? { ...prevState, ...updatedRow } : updatedRow,
        );
        setTaxExemptionActionMessage(res.data && res.data.message ? res.data.message : 'Tax exemption review saved.');
        loadOverview();
        loadTaxExemptionAudit(selectedTaxExemptionDocument.pk_TaxExemptionDocument);
      })
      .catch((err) => {
        setTaxExemptionActionMessage(
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Failed to save tax exemption review.',
        );
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsTaxExemptionActionLoading(false);
      });
  };

  const downloadTaxExemptionDocument = (document) => {
    if (!document || !document.pk_TaxExemptionDocument || !document.DocumentStorageKey) {
      return;
    }

    setTaxExemptionActionMessage('');
    setIsTaxExemptionActionLoading(true);

    getTokenSilently()
      .then((token) =>
        fetchDataAgnostic(
          `financial-intelligence/tax-exemptions/${document.pk_TaxExemptionDocument}/download`,
          {},
          formatBearerToken(token),
        ),
      )
      .then((res) => {
        if (res.data && res.data.uri) {
          window.open(res.data.uri, '_blank', 'noopener,noreferrer');
        }
        setTaxExemptionActionMessage('Opened secure tax exemption download.');
        loadTaxExemptionAudit(document.pk_TaxExemptionDocument);
      })
      .catch((err) => {
        setTaxExemptionActionMessage(
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Failed to open tax exemption document.',
        );
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsTaxExemptionActionLoading(false);
      });
  };

  const allocationOrgId =
    allocationEditor.lineItem && allocationEditor.lineItem.fk_Organization
      ? Number(allocationEditor.lineItem.fk_Organization)
      : selectedOrganizationId;

  const workspaceDepartments =
    invoiceWorkspace.mappingTargets && Array.isArray(invoiceWorkspace.mappingTargets.departments)
      ? invoiceWorkspace.mappingTargets.departments
      : [];
  const allocationDepartmentsSource = workspaceDepartments.length ? workspaceDepartments : billingDepartments;
  const allocationDepartments = allocationDepartmentsSource.filter((department) => {
    if (!allocationOrgId) {
      return true;
    }

    return Number(department.fk_Organization) === Number(allocationOrgId);
  });

  const workspaceSeasons =
    invoiceWorkspace.mappingTargets && Array.isArray(invoiceWorkspace.mappingTargets.seasons)
      ? invoiceWorkspace.mappingTargets.seasons
      : [];
  const seasonOptionsByDepartment = (workspaceSeasons.length ? workspaceSeasons : seasonStatuses).reduce((acc, season) => {
    const departmentKey = Number(season.fk_Department);
    if (!acc.has(departmentKey)) {
      acc.set(departmentKey, []);
    }

    acc.get(departmentKey).push({
      ...season,
      SeasonLabel: season.SeasonLabel || season.SeasonName,
    });
    return acc;
  }, new Map());

  const editableAllocations = allocationEditor.allocations || [];
  const editableAllocationTotals = editableAllocations.reduce(
    (acc, allocation) => {
      const quantity = allocation.AllocationQuantity === '' ? null : Number(allocation.AllocationQuantity);
      const amount = Number(allocation.AllocationAmount);

      if (Number.isFinite(quantity)) {
        acc.quantity += quantity;
      }

      if (Number.isFinite(amount)) {
        acc.amount += amount;
      }

      return acc;
    },
    { quantity: 0, amount: 0 }
  );

  const quickBooks = overview.quickBooks || fallbackOverview.quickBooks;
  const quickBooksConnection = quickBooks.connection || fallbackOverview.quickBooks.connection;
  const quickBooksActions = quickBooks.actions || fallbackOverview.quickBooks.actions;
  const quickBooksUiEnabled = quickBooksUiEnabledOverride === true || quickBooks.uiEnabled === true;
  const billingLockedUntilMapped =
    quickBooksUiEnabled && selectedOrganizationId && !selectedOrganizationMappedCustomer;
  const selectedInvoiceCloseOutState = getInvoiceCloseOutState(selectedInvoice);
  const closeOutDisabledReason = !quickBooksActions.canCloseOutInvoices
    ? 'Invoice close-out is behind a feature flag right now.'
    : getInvoiceCloseOutDisabledReason(selectedInvoice);
  const canRunCloseOutAction =
    quickBooksActions.canCloseOutInvoices &&
    selectedInvoice &&
    selectedInvoiceCloseOutState.status !== 'manual_only' &&
    selectedInvoiceCloseOutState.status !== 'confirmed' &&
    selectedInvoiceCloseOutState.status !== 'pending';
  const reportingByOrganization = (reporting.revenueByOrganization || []).filter((row) =>
    selectedOrganizationId ? Number(row.fk_Organization) === selectedOrganizationId : true,
  );
  const reportingByDepartment = (reporting.revenueByDepartment || []).filter((row) => {
    if (selectedDepartmentId) {
      return Number(row.fk_Department) === selectedDepartmentId;
    }

    if (selectedOrganizationId) {
      return Number(row.fk_Organization) === selectedOrganizationId;
    }

    return true;
  });
  const reportingLtvByOrganization = (reporting.ltvByOrganization || []).filter((row) =>
    selectedOrganizationId ? Number(row.fk_Organization) === selectedOrganizationId : true,
  );
  const reportingLtvByDepartment = (reporting.ltvByDepartment || []).filter((row) => {
    if (selectedDepartmentId) {
      return Number(row.fk_Department) === selectedDepartmentId;
    }

    if (selectedOrganizationId) {
      return Number(row.fk_Organization) === selectedOrganizationId;
    }

    return true;
  });
  const reportingPrepaidCoverage = (reporting.prepaidFutureCoverage || []).filter((row) => {
    if (selectedDepartmentId) {
      return Number(row.fk_Department) === selectedDepartmentId;
    }

    if (selectedOrganizationId) {
      return Number(row.fk_Organization) === selectedOrganizationId;
    }

    return true;
  });

  useEffect(() => {
    if (!selectedOrganization) {
      setActiveOrganizationTab(ORGANIZATION_TABS.departments);
      return;
    }

    if (activeOrganizationTab === ORGANIZATION_TABS.billing && !quickBooksUiEnabled) {
      setActiveOrganizationTab(ORGANIZATION_TABS.departments);
    }
  }, [activeOrganizationTab, quickBooksUiEnabled, selectedOrganization]);

  useEffect(() => {
    if (!selectedTaxExemptionDocument) {
      return;
    }

    const nextDocument = filteredTaxExemptionDocuments.find(
      (document) =>
        Number(document.pk_TaxExemptionDocument) === Number(selectedTaxExemptionDocument.pk_TaxExemptionDocument),
    );

    if (!nextDocument) {
      setSelectedTaxExemptionDocument(null);
      setTaxExemptionAudit([]);
      return;
    }

    if (nextDocument !== selectedTaxExemptionDocument) {
      setSelectedTaxExemptionDocument(nextDocument);
    }
  }, [filteredTaxExemptionDocuments, selectedTaxExemptionDocument]);

  const runQuickBooksAction = ({ action, environment = 'sandbox' }) => {
    setQuickBooksActionMessage('');
    setIsQuickBooksActionLoading(true);

    getTokenSilently()
      .then((token) => {
        const headers = formatBearerToken(token);

        if (action === 'connect') {
          const isLocalSandboxConnect =
            environment === 'sandbox' &&
            ['localhost', '127.0.0.1'].includes(window.location.hostname);

          return fetchDataAgnostic(
            'financial-intelligence/quickbooks/connect-url',
            isLocalSandboxConnect ? { environment, redirect: 'local' } : { environment },
            headers,
          );
        }

        if (action === 'disconnect') {
          return postDataAgnostic('financial-intelligence/quickbooks/disconnect', {}, { environment }, headers);
        }

        return postDataAgnostic('financial-intelligence/quickbooks/sync/invoices', {}, { environment }, headers);
      })
      .then((res) => {
        if (action === 'connect' && res.data && res.data.authUrl) {
          window.open(res.data.authUrl, '_blank', 'noopener,noreferrer');
          setQuickBooksActionMessage(`Opened QuickBooks ${environment} authorization.`);
        } else {
          setQuickBooksActionMessage(res.data && res.data.message ? res.data.message : 'QuickBooks action completed.');
        }
      })
      .catch((err) => {
        const message =
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'QuickBooks action is not available yet.';

        setQuickBooksActionMessage(message);
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsQuickBooksActionLoading(false);
      });
  };

  const runDraftInvoiceAction = () => {
    if (!selectedInvoice || !selectedInvoice.pk_QuickBooksInvoice) {
      setQuickBooksActionMessage('Select an imported invoice before creating a QuickBooks draft.');
      return;
    }

    setQuickBooksActionMessage('');
    setIsQuickBooksActionLoading(true);

    getTokenSilently()
      .then((token) =>
        postDataAgnostic(
          `financial-intelligence/quickbooks/invoices/${selectedInvoice.pk_QuickBooksInvoice}/create-draft`,
          {},
          {
            environment: quickBooksConnection.environment || 'sandbox',
            issueDate: draftIssueDate || undefined,
            dueDate: draftDueDate || undefined,
          },
          formatBearerToken(token),
        ),
      )
      .then((res) => {
        const draftInvoice = res.data && res.data.draftInvoice ? res.data.draftInvoice : null;
        const baseMessage =
          res.data && res.data.message ? res.data.message : 'QuickBooks draft invoice created.';

        setQuickBooksActionMessage(
          draftInvoice && draftInvoice.docNumber
            ? `${baseMessage} Draft #${draftInvoice.docNumber}.`
            : draftInvoice && draftInvoice.id
              ? `${baseMessage} QuickBooks id ${draftInvoice.id}.`
              : baseMessage,
        );
      })
      .catch((err) => {
        const message =
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'QuickBooks draft invoice creation failed.';

        setQuickBooksActionMessage(message);
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsQuickBooksActionLoading(false);
      });
  };

  const runInvoiceCloseOutAction = () => {
    if (!selectedInvoice || !selectedInvoice.pk_QuickBooksInvoice) {
      setQuickBooksActionMessage('Select an imported invoice before requesting a close-out.');
      return;
    }

    if (!closeOutReason.trim()) {
      setQuickBooksActionMessage('Enter a close-out reason before voiding the QuickBooks invoice.');
      return;
    }

    setQuickBooksActionMessage('');
    setIsQuickBooksActionLoading(true);

    getTokenSilently()
      .then((token) =>
        postDataAgnostic(
          `financial-intelligence/quickbooks/invoices/${selectedInvoice.pk_QuickBooksInvoice}/close-out`,
          {},
          {
            environment: quickBooksConnection.environment || 'sandbox',
            reason: closeOutReason.trim(),
          },
          formatBearerToken(token),
        ),
      )
      .then((res) => {
        const payload = res.data || {};
        const summary = payload.closeOutSummary || {};
        const invoice = payload.invoice || null;
        const countsMessage =
          summary.allocationCount || summary.inactivatedSeasonCount
            ? ` ${summary.allocationCount || 0} allocation${summary.allocationCount === 1 ? '' : 's'} canceled, ${summary.inactivatedSeasonCount || 0} future season${summary.inactivatedSeasonCount === 1 ? '' : 's'} inactivated.`
            : '';

        setQuickBooksActionMessage(
          `${payload.message || 'QuickBooks close-out submitted.'}${countsMessage}`,
        );
        if (invoice) {
          setSelectedInvoice((prevState) => ({
            ...(prevState || {}),
            ...invoice,
          }));
        }
        loadOverview();
      })
      .catch((err) => {
        const message =
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'QuickBooks close-out failed.';

        setQuickBooksActionMessage(message);
        if (err && err.response && err.response.data && err.response.data.invoice) {
          setSelectedInvoice((prevState) => ({
            ...(prevState || {}),
            ...(err.response.data.invoice || {}),
          }));
        }
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsQuickBooksActionLoading(false);
      });
  };

  const runCustomerMappingAction = ({ customer, action }) => {
    if (!customer || !customer.pk_QuickBooksCustomerMapping) {
      return;
    }

    if (action === 'map' && !selectedOrganizationId) {
      setQuickBooksActionMessage('Select a RezRATE organization before mapping a QuickBooks customer.');
      return;
    }

    setQuickBooksActionMessage('');
    setCustomerMappingLoadingMessage(
      action === 'map'
        ? `Mapping ${customer.QuickBooksCustomerName || 'customer'} to the selected organization...`
        : action === 'unmap'
          ? `Removing mapping for ${customer.QuickBooksCustomerName || 'customer'}...`
          : action === 'ignore'
            ? `Ignoring ${customer.QuickBooksCustomerName || 'customer'}...`
            : `Creating organization from ${customer.QuickBooksCustomerName || 'customer'}...`,
    );
    setIsQuickBooksActionLoading(true);

    getTokenSilently()
      .then((token) => {
        const headers = formatBearerToken(token);
        const baseUrl = `financial-intelligence/quickbooks/customers/${customer.pk_QuickBooksCustomerMapping}`;

        if (action === 'map') {
          return postDataAgnostic(
            `${baseUrl}/map`,
            {},
            { fk_Organization: selectedOrganizationId, fk_Department: selectedDepartmentId || null },
            headers,
          );
        }

        if (action === 'unmap') {
          return postDataAgnostic(`${baseUrl}/unmap`, {}, {}, headers);
        }

        if (action === 'createOrganization') {
          return postDataAgnostic(
            `${baseUrl}/create-organization`,
            {},
            { OrganizationName: customer.QuickBooksCustomerName },
            headers,
          );
        }

        return postDataAgnostic(`${baseUrl}/ignore`, {}, {}, headers);
      })
      .then((res) => {
        setQuickBooksActionMessage(res.data && res.data.message ? res.data.message : 'Customer mapping updated.');
        loadOverview();
      })
      .catch((err) => {
        const message =
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Customer mapping action failed.';

        setQuickBooksActionMessage(message);
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setCustomerMappingLoadingMessage('');
        setIsQuickBooksActionLoading(false);
      });
  };

  const toggleMappingAudit = (customer) => {
    const pk = customer && customer.pk_QuickBooksCustomerMapping;
    if (!pk) return;

    if (mappingAuditFor === pk) {
      setMappingAuditFor(null);
      setMappingAuditRows([]);
      return;
    }

    setMappingAuditFor(pk);
    setMappingAuditRows([]);

    getTokenSilently()
      .then((token) =>
        fetchDataAgnostic(`financial-intelligence/quickbooks/customers/${pk}/mapping-audit`, {}, formatBearerToken(token)),
      )
      .then((res) => {
        setMappingAuditRows(Array.isArray(res.data && res.data.rows) ? res.data.rows : []);
      })
      .catch((err) => {
        setMappingAuditRows([]);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const toggleAllocationAudit = (allocation) => {
    const pk = allocation && allocation.pk_QuickBooksInvoiceAllocation;
    if (!pk) return;

    if (allocationAuditFor === pk) {
      setAllocationAuditFor(null);
      setAllocationAuditRows([]);
      return;
    }

    setAllocationAuditFor(pk);
    setAllocationAuditRows([]);

    getTokenSilently()
      .then((token) =>
        fetchDataAgnostic(`financial-intelligence/quickbooks/allocations/${pk}/audit`, {}, formatBearerToken(token)),
      )
      .then((res) => {
        setAllocationAuditRows(Array.isArray(res.data && res.data.rows) ? res.data.rows : []);
      })
      .catch((err) => {
        setAllocationAuditRows([]);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const runSeasonStatusOverride = (season, presetStatus = null) => {
    const pk = season && season.pk_Season;
    if (!pk) return;

    const status = presetStatus
      ? presetStatus
      : window.prompt(
          `Override financial status for "${season.SeasonLabel || 'this season'}". Valid values:\n${SEASON_FINANCIAL_STATUSES.join(', ')}`,
          season.FinancialStatus || '',
        );
    if (!status) return;

    if (!SEASON_FINANCIAL_STATUSES.includes(status)) {
      setSeasonOverrideMessage(`"${status}" is not a recognized financial status.`);
      return;
    }

    const reason = window.prompt(
      presetStatus
        ? `Why does "${season.SeasonLabel || 'this season'}" need review? (for the audit trail):`
        : 'Optional reason for the audit trail:',
      '',
    );
    if (reason === null) return;

    setSeasonOverrideMessage('');
    setIsSeasonOverrideLoading(true);

    getTokenSilently()
      .then((token) =>
        postDataAgnostic(
          `financial-intelligence/seasons/${pk}/status-override`,
          {},
          { status, reason: reason || undefined },
          formatBearerToken(token),
        ),
      )
      .then((res) => {
        setSeasonOverrideMessage(res.data && res.data.message ? res.data.message : 'Season status overridden.');
        loadOverview();
      })
      .catch((err) => {
        const message =
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Failed to override season status.';
        setSeasonOverrideMessage(message);
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsSeasonOverrideLoading(false);
      });
  };

  const clearSeasonStatusOverride = (season) => {
    const pk = season && season.pk_Season;
    if (!pk) return;

    if (!window.confirm(`Clear the manual status override for "${season.SeasonLabel || 'this season'}"?`)) {
      return;
    }

    setSeasonOverrideMessage('');
    setIsSeasonOverrideLoading(true);

    getTokenSilently()
      .then((token) =>
        deleteData(`financial-intelligence/seasons/${pk}/status-override`, {}, {}, formatBearerToken(token)),
      )
      .then((res) => {
        setSeasonOverrideMessage(res.data && res.data.message ? res.data.message : 'Override cleared.');
        loadOverview();
      })
      .catch((err) => {
        const message =
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Failed to clear season status override.';
        setSeasonOverrideMessage(message);
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsSeasonOverrideLoading(false);
      });
  };

  const toggleSeasonOverrideHistory = (season) => {
    const pk = season && season.pk_Season;
    if (!pk) return;

    if (seasonOverrideHistoryFor === pk) {
      setSeasonOverrideHistoryFor(null);
      setSeasonOverrideHistoryRows([]);
      return;
    }

    setSeasonOverrideHistoryFor(pk);
    setSeasonOverrideHistoryRows([]);

    getTokenSilently()
      .then((token) =>
        fetchDataAgnostic(`financial-intelligence/seasons/${pk}/status-override/history`, {}, formatBearerToken(token)),
      )
      .then((res) => {
        setSeasonOverrideHistoryRows(Array.isArray(res.data && res.data.rows) ? res.data.rows : []);
      })
      .catch((err) => {
        setSeasonOverrideHistoryRows([]);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const runReviewQueueAction = ({ item, action }) => {
    let payload = {};

    if (action === 'resolve') {
      const notes = window.prompt('Optional resolution notes for the audit trail:', item.ResolutionNotes || '');
      if (notes === null) {
        return;
      }

      payload = notes ? { notes } : {};
    }

    if (action === 'dismiss') {
      const reason = window.prompt('Optional dismissal reason for the audit trail:', '');
      if (reason === null) {
        return;
      }

      payload = reason ? { reason } : {};
    }

    setQuickBooksActionMessage('');
    setIsReviewQueueLoading(true);

    getTokenSilently()
      .then((token) => {
        const headers = formatBearerToken(token);

        if (action === 'reconcile') {
          return postDataAgnostic('financial-intelligence/review-queue/reconcile', {}, {}, headers);
        }

        const baseUrl = `financial-intelligence/review-queue/${item.pk_FinancialReviewQueueItem}`;
        if (action === 'resolve') {
          return postDataAgnostic(`${baseUrl}/resolve`, {}, payload, headers);
        }

        return postDataAgnostic(`${baseUrl}/dismiss`, {}, payload, headers);
      })
      .then((res) => {
        setQuickBooksActionMessage(res.data && res.data.message ? res.data.message : 'Review queue updated.');
        loadOverview();
      })
      .catch((err) => {
        const message =
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Review queue action failed.';

        setQuickBooksActionMessage(message);
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsReviewQueueLoading(false);
      });
  };

  const focusInvoice = (invoice) => {
    if (!invoice) {
      return;
    }

    setSelectedInvoice(invoice);
    if (invoice.fk_Organization) {
      setSelectedOrganization(`${invoice.fk_Organization}`);
    }
  };

  const loadInvoiceAllocationWorkspace = useCallback(
    (invoice, options = {}) => {
      if (!invoice || !invoice.pk_QuickBooksInvoice) {
        setInvoiceWorkspace({
          invoice: null,
          lineItems: [],
          warnings: [],
          guidance: null,
          mappingTargets: { departments: [], seasons: [] },
        });
        return;
      }

      setIsWorkspaceLoading(true);

      getTokenSilently()
        .then((token) =>
          fetchDataAgnostic(
            `financial-intelligence/quickbooks/invoices/${invoice.pk_QuickBooksInvoice}/allocation-workspace`,
            {},
            formatBearerToken(token),
          ),
        )
        .then((res) => {
          const payload = res.data || {};
          const nextLineItems = Array.isArray(payload.lineItems) ? payload.lineItems : [];
          setInvoiceWorkspace({
            invoice: payload.invoice || invoice,
            lineItems: nextLineItems,
            warnings: Array.isArray(payload.warnings) ? payload.warnings : [],
            guidance: payload.guidance || null,
            mappingTargets: payload.mappingTargets || { departments: [], seasons: [] },
          });

          if (options.focusLineItemPk) {
            const matching = nextLineItems.find(
              (lineItem) =>
                Number(lineItem.pk_QuickBooksInvoiceLineItem) === Number(options.focusLineItemPk),
            );
            if (matching) {
              loadLineItemAllocations(matching);
            }
          }
        })
        .catch((err) => {
          setInvoiceWorkspace({
            invoice,
            lineItems: [],
            warnings: [],
            guidance: null,
            mappingTargets: { departments: [], seasons: [] },
          });
          setAllocationMessage(
            err && err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : 'Failed to load invoice allocation workspace.',
          );
          checkLoginForError({ err, loginWithRedirect });
        })
        .finally(() => {
          setIsWorkspaceLoading(false);
        });
    },
    [getTokenSilently, loginWithRedirect],
  );

  const focusLineItem = (lineItem) => {
    if (!lineItem) {
      return;
    }

    const invoice = invoicesByPk[Number(lineItem.fk_QuickBooksInvoice)];
    if (invoice) {
      setSelectedInvoice(invoice);
      if (invoice.fk_Organization) {
        setSelectedOrganization(`${invoice.fk_Organization}`);
      }
    }
  };

  const focusCustomerMapping = (item) => {
    if (!item) {
      return;
    }

    if (item.fk_Organization) {
      setSelectedOrganization(`${item.fk_Organization}`);
    }
    if (item.fk_Department) {
      setSelectedDepartment(`${item.fk_Department}`);
    }
    setCustomerFilter('all');
    setCustomerSearch(item.QuickBooksCustomerName || '');
  };

  const focusSeasonCoverage = (item) => {
    if (!item) {
      return;
    }

    if (item.fk_Organization) {
      setSelectedOrganization(`${item.fk_Organization}`);
    }
    if (item.fk_Department) {
      setSelectedDepartment(`${item.fk_Department}`);
    }
  };

  const loadLineItemAllocations = (lineItem) => {
    if (!lineItem || !lineItem.pk_QuickBooksInvoiceLineItem) {
      return;
    }

    setAllocationMessage('');
    setIsAllocationLoading(true);
    setSelectedLineItem(lineItem);

    getTokenSilently()
      .then((token) =>
        fetchDataAgnostic(
          `financial-intelligence/quickbooks/line-items/${lineItem.pk_QuickBooksInvoiceLineItem}/allocations`,
          {},
          formatBearerToken(token),
        ),
      )
      .then((res) => {
        const payload = res.data || {};
        const nextAllocations = Array.isArray(payload.allocations)
          ? payload.allocations.map((allocation) => ({
              ...allocation,
              fk_Department: allocation.fk_Department ? `${allocation.fk_Department}` : '',
              fk_Season: allocation.fk_Season ? `${allocation.fk_Season}` : '',
              AllocationQuantity:
                allocation.AllocationQuantity === null || allocation.AllocationQuantity === undefined
                  ? ''
                  : `${allocation.AllocationQuantity}`,
              AllocationAmount:
                allocation.AllocationAmount === null || allocation.AllocationAmount === undefined
                  ? ''
                  : `${allocation.AllocationAmount}`,
              AllocationType: allocation.AllocationType || 'amount',
              CoverageStatus: allocation.CoverageStatus || 'pending',
              Notes: allocation.Notes || '',
            }))
          : [];

        setAllocationEditor({
          lineItem: payload.lineItem || null,
          allocations: nextAllocations,
          totals: payload.totals || null,
        });
      })
      .catch((err) => {
        setAllocationEditor({
          lineItem: null,
          allocations: [],
          totals: null,
        });
        setAllocationMessage(
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Failed to load allocation details.',
        );
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsAllocationLoading(false);
      });
  };

  useEffect(() => {
    if (selectedInvoice && selectedInvoice.pk_QuickBooksInvoice) {
      loadInvoiceAllocationWorkspace(selectedInvoice);
    }
  }, [selectedInvoice, loadInvoiceAllocationWorkspace]);

  const buildDefaultAllocationDraft = () => {
    const fallbackDepartment = selectedDepartmentId
      ? allocationDepartments.find((department) => Number(department.pk_Department) === Number(selectedDepartmentId))
      : allocationDepartments[0];
    const departmentId = fallbackDepartment ? `${fallbackDepartment.pk_Department}` : '';
    const seasonOptions = departmentId ? seasonOptionsByDepartment.get(Number(departmentId)) || [] : [];
    const defaultSeason = seasonOptions[0];

    return {
      pk_QuickBooksInvoiceAllocation: null,
      fk_Department: departmentId,
      fk_Season: defaultSeason ? `${defaultSeason.pk_Season}` : '',
      AllocationQuantity: '',
      AllocationAmount: '',
      AllocationType: 'amount',
      CoverageStatus: 'pending',
      Notes: '',
    };
  };

  const addAllocationDraft = () => {
    setAllocationEditor((prevState) => ({
      ...prevState,
      allocations: [...(prevState.allocations || []), buildDefaultAllocationDraft()],
    }));
  };

  const updateAllocationDraft = (index, key, value) => {
    setAllocationEditor((prevState) => {
      const nextAllocations = [...(prevState.allocations || [])];
      const target = { ...(nextAllocations[index] || {}) };
      target[key] = value;

      if (key === 'fk_Department') {
        const availableSeasons = seasonOptionsByDepartment.get(Number(value)) || [];
        const seasonStillValid = availableSeasons.some(
          (season) => Number(season.pk_Season) === Number(target.fk_Season),
        );

        if (!seasonStillValid) {
          target.fk_Season = availableSeasons[0] ? `${availableSeasons[0].pk_Season}` : '';
        }
      }

      nextAllocations[index] = target;
      return {
        ...prevState,
        allocations: nextAllocations,
      };
    });
  };

  const removeAllocationDraft = (index) => {
    setAllocationEditor((prevState) => ({
      ...prevState,
      allocations: (prevState.allocations || []).filter((_, allocationIndex) => allocationIndex !== index),
    }));
  };

  const saveAllocations = () => {
    if (!allocationEditor.lineItem || !allocationEditor.lineItem.pk_QuickBooksInvoiceLineItem) {
      return;
    }

    const allocations = editableAllocations.map((allocation) => ({
      pk_QuickBooksInvoiceAllocation: allocation.pk_QuickBooksInvoiceAllocation || undefined,
      fk_Department: Number(allocation.fk_Department),
      fk_Season: Number(allocation.fk_Season),
      AllocationQuantity: allocation.AllocationQuantity === '' ? null : Number(allocation.AllocationQuantity),
      AllocationAmount: Number(allocation.AllocationAmount),
      AllocationType: allocation.AllocationType || 'amount',
      CoverageStatus: allocation.CoverageStatus || 'pending',
      Notes: allocation.Notes || null,
    }));

    const invalidAllocation = allocations.find(
      (allocation) =>
        !Number.isInteger(allocation.fk_Department) ||
        allocation.fk_Department <= 0 ||
        !Number.isInteger(allocation.fk_Season) ||
        allocation.fk_Season <= 0 ||
        !Number.isFinite(allocation.AllocationAmount),
    );

    if (invalidAllocation) {
      setAllocationMessage('Each allocation needs a department, season, and numeric amount before saving.');
      return;
    }

    setAllocationMessage('');
    setIsAllocationSaving(true);

    getTokenSilently()
      .then((token) =>
        postDataAgnostic(
          `financial-intelligence/quickbooks/line-items/${allocationEditor.lineItem.pk_QuickBooksInvoiceLineItem}/allocations`,
          {},
          { allocations },
          formatBearerToken(token),
        ),
      )
      .then((res) => {
        setAllocationMessage(res.data && res.data.message ? res.data.message : 'Allocations saved.');
        loadOverview();
        if (selectedInvoice) {
          loadInvoiceAllocationWorkspace(selectedInvoice, {
            focusLineItemPk: allocationEditor.lineItem && allocationEditor.lineItem.pk_QuickBooksInvoiceLineItem,
          });
        }
        loadLineItemAllocations(selectedLineItem || allocationEditor.lineItem);
      })
      .catch((err) => {
        setAllocationMessage(
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Failed to save allocations.',
        );
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsAllocationSaving(false);
      });
  };

  const markAllocationReconciled = (index) => {
    updateAllocationDraft(index, 'CoverageStatus', 'reconciled');
  };

  const ensurePseudoLineItem = () => {
    if (!selectedInvoice || !selectedInvoice.pk_QuickBooksInvoice) {
      return;
    }

    setAllocationMessage('');
    setIsWorkspaceActionLoading(true);

    getTokenSilently()
      .then((token) =>
        postDataAgnostic(
          `financial-intelligence/quickbooks/invoices/${selectedInvoice.pk_QuickBooksInvoice}/ensure-pseudo-line-item`,
          {},
          {},
          formatBearerToken(token),
        ),
      )
      .then((res) => {
        setAllocationMessage(
          res.data && res.data.message ? res.data.message : 'Pseudo line item created.',
        );
        loadOverview();
        loadInvoiceAllocationWorkspace(selectedInvoice, {
          focusLineItemPk: res.data && res.data.lineItem ? res.data.lineItem.pk_QuickBooksInvoiceLineItem : null,
        });
      })
      .catch((err) => {
        setAllocationMessage(
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Failed to create pseudo line item.',
        );
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsWorkspaceActionLoading(false);
      });
  };

  const renderDisabledWorkflows = () => {
    const templates = overview.billingWorkflowTemplates || fallbackOverview.billingWorkflowTemplates;

    return (
      <div style={style.workflowActions}>
        {templates.map((template) => (
          <button key={template.key} type="button" style={style.disabledActionButton} disabled>
            <FontAwesomeIcon icon={template.icon || faFileInvoiceDollar} />
            {template.label}
          </button>
        ))}
      </div>
    );
  };

  const renderOrganizationTabs = () => {
    if (!selectedOrganization) {
      return null;
    }

    const tabs = [
      {
        key: ORGANIZATION_TABS.departments,
        label: 'Departments',
      },
      ...(quickBooksUiEnabled
        ? [
            {
              key: ORGANIZATION_TABS.billing,
              label: 'Billing',
            },
          ]
        : []),
    ];

    return (
      <div style={style.organizationTabRow}>
        {tabs.map((tab) => {
          const isActive = activeOrganizationTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              style={isActive ? style.organizationTabButtonActive : style.organizationTabButton}
              onClick={() => setActiveOrganizationTab(tab.key)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    );
  };

  const renderDepartmentWorkspace = () => {
    if (!selectedOrganization && !selectedDepartment) {
      return (
        <div style={EntityStyle.departmentWrapperWidget}>
          <div className="flex-center" style={{ height: '100%', textAlign: 'center' }}>
            Select an organization and department, or add a new department
          </div>
        </div>
      );
    }

    if (!selectedDepartment && !isCreatingDepartment) {
      return (
        <div style={EntityStyle.departmentWrapperWidget}>
          <div className="flex-center" style={{ height: '100%', textAlign: 'center' }}>
            Select a department from the organization list, or choose Add New Department.
          </div>
        </div>
      );
    }

    return (
      <DepartmentCreationAndEdit
        getDepartments={getDepartments}
        selectedDepartment={selectedDepartment}
        departments={departments}
        departmentReplyToAddress={departmentReplyToAddress}
        setDepartmentReplyToAddress={setDepartmentReplyToAddress}
        departmentReplyToName={departmentReplyToName}
        setDepartmentReplyToName={setDepartmentReplyToName}
        selectedOrganization={selectedOrganization}
        departmentLogo={departmentLogo}
        setDepartmentLogo={setDepartmentLogo}
        departmentName={departmentName}
        setDepartmentName={setDepartmentName}
        departmentShortName={departmentShortName}
        setDepartmentShortName={setDepartmentShortName}
        organizations={organizations}
        departmentWebsite={departmentWebsite}
        setDepartmentWebsite={setDepartmentWebsite}
        setSelectedDepartment={setSelectedDepartment}
        setDepartments={setDepartments}
      />
    );
  };

  const renderQuickBooksStatusPanel = () => {
    if (!quickBooksUiEnabled) {
      return null;
    }

    const environmentLabel = formatValue(quickBooksConnection.environment || 'Sandbox');
    const mappingLabel = selectedOrganizationMappedCustomer
      ? selectedOrganizationMappedCustomer.QuickBooksCustomerName || selectedOrganizationMappedCustomer.OrganizationName
      : 'Not mapped yet';

    return (
      <div style={style.quickBooksPanel}>
        <div style={style.relationHeader}>
          <div>
            <div style={style.relationTitle}>QuickBooks billing</div>
            <div style={style.contextNote}>
              Keep this lightweight: connect sandbox, map the organization, then the rest of Billing becomes available.
            </div>
          </div>
          <div style={style.statusPill}>{formatValue(quickBooksConnection.status)}</div>
        </div>
        <div style={style.billingSummaryGrid}>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Environment</div>
            <div style={style.statusValue}>{environmentLabel}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Mapped customer</div>
            <div style={style.statusValue}>{formatValue(mappingLabel)}</div>
          </div>
        </div>
        {quickBooksConnection.lastSyncError ? (
          <div style={style.warningBanner}>
            <FontAwesomeIcon icon={faExclamationTriangle} /> {quickBooksConnection.lastSyncError}
          </div>
        ) : null}
        <div style={style.workflowActions}>
          {quickBooksConnection.status !== 'connected' ? (
            <button
              type="button"
              style={quickBooksActions.canConnect ? style.actionButton : style.disabledActionButton}
              disabled={!quickBooksActions.canConnect || isQuickBooksActionLoading}
              onClick={() => runQuickBooksAction({ action: 'connect', environment: 'sandbox' })}
            >
              <FontAwesomeIcon icon={faPlug} />
              Connect Sandbox
            </button>
          ) : null}
          <button
            type="button"
            style={quickBooksActions.canImportInvoices ? style.actionButton : style.disabledActionButton}
            disabled={!quickBooksActions.canImportInvoices || isQuickBooksActionLoading}
            onClick={() => runQuickBooksAction({ action: 'sync', environment: 'sandbox' })}
          >
            <FontAwesomeIcon icon={faSyncAlt} />
            Import Sandbox Invoices
          </button>
          <button
            type="button"
            style={quickBooksActions.canDisconnect ? style.actionButton : style.disabledActionButton}
            disabled={!quickBooksActions.canDisconnect || isQuickBooksActionLoading}
            onClick={() => runQuickBooksAction({ action: 'disconnect', environment: 'sandbox' })}
          >
            <FontAwesomeIcon icon={faUnlink} />
            Disconnect
          </button>
          <button type="button" style={style.disabledActionButton} disabled>
            <FontAwesomeIcon icon={faPlug} />
            Connect Production
          </button>
        </div>
        {quickBooksActionMessage ? <div style={style.actionMessage}>{quickBooksActionMessage}</div> : null}
      </div>
    );
  };

  const renderCustomerMappings = () => {
    if (!quickBooksUiEnabled) {
      return null;
    }

    const selectedDepartmentForMapping = departmentById[selectedDepartmentId];
    const mappingTargetNote = selectedOrganizationRecord
      ? selectedDepartmentForMapping
        ? `Map one imported customer to ${selectedOrganizationRecord.OrganizationName} / ${selectedDepartmentForMapping.DepartmentName}.`
        : `Map one imported customer to ${selectedOrganizationRecord.OrganizationName}. You can optionally select a department in the sidebar to also link a program.`
      : 'Select a RezRATE organization in the sidebar (and optionally a department) before mapping a QuickBooks customer.';

    return (
      <div style={style.relationPanel}>
        <div style={style.relationHeader}>
          <div style={style.relationTitle}>QuickBooks customer mapping</div>
          <div style={style.contextNote}>{mappingTargetNote}</div>
        </div>
        {customerMappingLoadingMessage ? (
          <div style={style.mappingLoadingOverlay} role="status" aria-live="polite">
            <div style={style.mappingLoadingCard}>
              <FontAwesomeIcon icon={faSyncAlt} spin />
              <div style={style.mappingLoadingTitle}>Updating customer mapping</div>
              <div style={style.mappingLoadingBody}>{customerMappingLoadingMessage}</div>
            </div>
          </div>
        ) : null}
        <div style={style.toolbar}>
          <FontAwesomeIcon icon={faSearch} />
          <input
            aria-label="Search QuickBooks customers"
            style={style.toolbarInput}
            value={customerSearch}
            placeholder="Search QuickBooks customers"
            onChange={(e) => {
              setCustomerSearch(e.target.value);
            }}
          />
          <select
            aria-label="Customer mapping filter"
            style={style.toolbarSelect}
            value={customerFilter}
            onChange={(e) => {
              setCustomerFilter(e.target.value);
            }}
          >
            <option value="unmapped">Unmapped</option>
            <option value="mapped">Mapped</option>
            <option value="ignored">Ignored</option>
            <option value="all">All Customers</option>
          </select>
        </div>
        <div style={style.tableWrap}>
          <table style={style.table}>
            <thead>
              <tr>
                {(overview.customerColumns || fallbackOverview.customerColumns).map((column) => (
                  <th key={column.key} style={style.th}>
                    {column.label}
                  </th>
                ))}
                <th style={style.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleCustomers.length > 0 ? (
                visibleCustomers.map((customer) => (
                  <React.Fragment key={customer.pk_QuickBooksCustomerMapping}>
                  <tr>
                    {(overview.customerColumns || fallbackOverview.customerColumns).map((column) => (
                      <td key={column.key} style={style.td}>
                        {formatValue(customer[column.key])}
                      </td>
                    ))}
                    <td style={style.td}>
                      <div style={style.inlineActions}>
                        <button
                          type="button"
                          aria-label="Map to selected organization"
                          title="Map to selected organization"
                          style={selectedOrganizationId ? style.iconActionButton : style.iconDisabledActionButton}
                          disabled={!selectedOrganizationId || isQuickBooksActionLoading}
                          onClick={() => runCustomerMappingAction({ customer, action: 'map' })}
                        >
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </button>
                        <button
                          type="button"
                          aria-label="Unmap customer"
                          title="Unmap customer"
                          style={style.iconActionButton}
                          disabled={isQuickBooksActionLoading}
                          onClick={() => runCustomerMappingAction({ customer, action: 'unmap' })}
                        >
                          <FontAwesomeIcon icon={faUnlink} />
                        </button>
                        <button
                          type="button"
                          aria-label="Ignore customer"
                          title="Ignore customer"
                          style={style.iconActionButton}
                          disabled={isQuickBooksActionLoading}
                          onClick={() => runCustomerMappingAction({ customer, action: 'ignore' })}
                        >
                          <FontAwesomeIcon icon={faBan} />
                        </button>
                        <button
                          type="button"
                          aria-label="Create organization from customer"
                          title="Create organization from customer"
                          style={style.iconActionButton}
                          disabled={isQuickBooksActionLoading}
                          onClick={() => runCustomerMappingAction({ customer, action: 'createOrganization' })}
                        >
                          <FontAwesomeIcon icon={faPlusCircle} />
                        </button>
                        <button
                          type="button"
                          aria-label="View mapping audit history"
                          title="View mapping audit history"
                          style={style.iconActionButton}
                          onClick={() => toggleMappingAudit(customer)}
                        >
                          <FontAwesomeIcon icon={faHistory} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {mappingAuditFor === customer.pk_QuickBooksCustomerMapping ? (
                    <tr>
                      <td colSpan={(overview.customerColumns || fallbackOverview.customerColumns).length + 1}>
                        {mappingAuditRows.length > 0 ? (
                          <div style={style.taxExemptionAuditList}>
                            {mappingAuditRows.map((entry) => (
                              <div key={entry.pk_QuickBooksCustomerMappingAudit} style={style.taxExemptionAuditRow}>
                                <div style={style.taxExemptionAuditMeta}>
                                  <strong>{formatValue(`${entry.Action || ''}`.replace(/_/g, ' '))}</strong>
                                  <span>{formatDateLabel(entry.PerformedAt)}</span>
                                  <span>{formatValue(entry.PerformedBy)}</span>
                                </div>
                                <div style={style.taxExemptionAuditMeta}>
                                  <span>
                                    {entry.PreviousMappingStatus ? `From: ${entry.PreviousMappingStatus}` : 'From: --'}
                                  </span>
                                  <span>{entry.NewMappingStatus ? `To: ${entry.NewMappingStatus}` : 'To: --'}</span>
                                </div>
                                {entry.Notes ? <div style={style.taxExemptionNoteText}>{entry.Notes}</div> : null}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={style.emptyState}>No audit history yet for this mapping.</div>
                        )}
                      </td>
                    </tr>
                  ) : null}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={(overview.customerColumns || fallbackOverview.customerColumns).length + 1}>
                    <div style={style.emptyState}>No QuickBooks customers match this filter.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderBillingOnboarding = () => {
    if (!quickBooksUiEnabled) {
      return null;
    }

    const organizationName = selectedOrganizationRecord && selectedOrganizationRecord.OrganizationName
      ? selectedOrganizationRecord.OrganizationName
      : 'this organization';
    const mappedCustomerName = selectedOrganizationMappedCustomer
      ? selectedOrganizationMappedCustomer.QuickBooksCustomerName || selectedOrganizationMappedCustomer.OrganizationName
      : null;

    return (
      <div style={style.onboardingPanel}>
        <div style={style.onboardingHeader}>
          <div style={style.relationTitle}>How Billing works</div>
          <div style={style.contextNote}>
            Billing is customer-based. An organization needs one QuickBooks customer mapping before invoice tools appear.
          </div>
        </div>
        <div style={style.onboardingSteps}>
          <div style={style.onboardingStep}>
            <div style={style.onboardingStepNumber}>1</div>
            <div style={style.onboardingStepBody}>
              <div style={style.onboardingStepTitle}>Map the organization to a QuickBooks customer</div>
              <div style={style.contextNote}>
                Choose the imported QuickBooks customer that represents {organizationName}.
              </div>
            </div>
          </div>
          <div style={style.onboardingStep}>
            <div style={style.onboardingStepNumber}>2</div>
            <div style={style.onboardingStepBody}>
              <div style={style.onboardingStepTitle}>Then review invoices under that customer</div>
              <div style={style.contextNote}>
                After mapping, imported invoices, allocations, payments, and review items show up in this billing workspace.
              </div>
            </div>
          </div>
        </div>
        {mappedCustomerName ? (
          <div style={style.onboardingSuccess}>
            {organizationName} is currently mapped to <strong>{mappedCustomerName}</strong>.
          </div>
        ) : (
          <div style={style.onboardingHint}>
            There is no direct invoice-to-organization mapping step yet. Start with customer mapping below.
          </div>
        )}
      </div>
    );
  };

  const renderReviewQueue = () => {
    return (
      <div style={style.relationPanel}>
        <div style={style.relationHeader}>
          <div>
            <div style={style.relationTitle}>Review queue</div>
            <div style={style.contextNote}>Reconcile imported billing data and clear resolved items.</div>
            <div style={style.auditTrailNote}>
              <FontAwesomeIcon icon={faStickyNote} />
              Resolve and dismiss actions can include optional audit notes.
            </div>
          </div>
          <div style={style.inlineActions}>
            <button
              type="button"
              style={style.actionButton}
              disabled={isReviewQueueLoading}
              onClick={() => runReviewQueueAction({ action: 'reconcile' })}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={isReviewQueueLoading} />
              Reconcile
            </button>
            <select
              aria-label="Review queue filter"
              style={style.toolbarSelect}
              value={reviewFilter}
              onChange={(e) => {
                setReviewFilter(e.target.value);
              }}
            >
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
              <option value="all">All Items</option>
            </select>
            <select
              aria-label="Review queue type filter"
              style={style.toolbarSelect}
              value={reviewTypeFilter}
              onChange={(e) => {
                setReviewTypeFilter(e.target.value);
              }}
            >
              <option value="all">All Types</option>
              {(overview.reviewQueueTypes || []).map((type) => (
                <option key={type.key} value={type.key}>
                  {type.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Review queue age filter"
              style={style.toolbarSelect}
              value={reviewAgeFilter}
              onChange={(e) => {
                setReviewAgeFilter(e.target.value);
              }}
            >
              <option value="all">Any Age</option>
              <option value="0to7">0-7 days</option>
              <option value="8to30">8-30 days</option>
              <option value="31plus">31+ days</option>
            </select>
          </div>
        </div>
        <div style={style.tableWrap}>
          <table style={style.table}>
            <thead>
              <tr>
                {(overview.reviewQueueColumns || fallbackOverview.reviewQueueColumns).map((column) => (
                  <th key={column.key} style={style.th}>
                    {column.label}
                  </th>
                ))}
                <th style={style.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleReviewQueue.length > 0 ? (
                visibleReviewQueue.map((item) => (
                  <tr key={item.pk_FinancialReviewQueueItem}>
                    {(overview.reviewQueueColumns || fallbackOverview.reviewQueueColumns).map((column) => (
                      <td key={column.key} style={style.td}>
                        {formatValue(item[column.key])}
                      </td>
                    ))}
                    <td style={style.td}>
                      <div style={style.inlineActions}>
                        {quickBooksUiEnabled ? (() => {
                          const linkedInvoice = item.fk_QuickBooksInvoice ? invoicesByPk[Number(item.fk_QuickBooksInvoice)] : null;
                          const quickBooksUrl = item.QuickBooksViewUrl || getQuickBooksInvoiceUrl(linkedInvoice);

                          if (!quickBooksUrl) {
                            return null;
                          }

                          return (
                            <button
                              type="button"
                              aria-label="Open invoice in QuickBooks"
                              title="Open invoice in QuickBooks"
                              style={style.iconActionButton}
                              onClick={() => openQuickBooksUrl(quickBooksUrl)}
                            >
                              <FontAwesomeIcon icon={faExternalLinkAlt} />
                            </button>
                          );
                        })() : null}
                        {item.fk_QuickBooksInvoiceLineItem ? (
                          <button
                            type="button"
                            style={style.actionButton}
                            onClick={() => {
                              const lineItem = (overview.lineItems || []).find(
                                (candidate) =>
                                  Number(candidate.pk_QuickBooksInvoiceLineItem) === Number(item.fk_QuickBooksInvoiceLineItem),
                              );
                              if (lineItem) {
                                focusLineItem(lineItem);
                                loadLineItemAllocations(lineItem);
                              }
                            }}
                          >
                            Focus line item
                          </button>
                        ) : null}
                        {!item.fk_QuickBooksInvoiceLineItem && item.fk_QuickBooksInvoice ? (
                          <button
                            type="button"
                            style={style.actionButton}
                            onClick={() => {
                              const invoice = invoicesByPk[Number(item.fk_QuickBooksInvoice)];
                              if (invoice) {
                                focusInvoice(invoice);
                              }
                            }}
                          >
                            Focus invoice
                          </button>
                        ) : null}
                        {item.fk_QuickBooksCustomerMapping ? (
                          <button
                            type="button"
                            style={style.actionButton}
                            onClick={() => focusCustomerMapping(item)}
                          >
                            Focus mapping
                          </button>
                        ) : null}
                        {item.fk_Season ? (
                          <button
                            type="button"
                            style={style.actionButton}
                            onClick={() => focusSeasonCoverage(item)}
                          >
                            Focus season
                          </button>
                        ) : null}
                        <button
                          type="button"
                          aria-label="Resolve review item"
                          title="Resolve review item"
                          style={item.Status === 'open' ? style.iconActionButton : style.iconDisabledActionButton}
                          disabled={isReviewQueueLoading || item.Status !== 'open'}
                          onClick={() => runReviewQueueAction({ item, action: 'resolve' })}
                        >
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </button>
                        <button
                          type="button"
                          aria-label="Dismiss review item"
                          title="Dismiss review item"
                          style={item.Status === 'open' ? style.iconActionButton : style.iconDisabledActionButton}
                          disabled={isReviewQueueLoading || item.Status !== 'open'}
                          onClick={() => runReviewQueueAction({ item, action: 'dismiss' })}
                        >
                          <FontAwesomeIcon icon={faTimesCircle} />
                        </button>
                      </div>
                      {(item.ResolvedAt || item.ResolvedBy || item.ResolutionNotes) && (
                        <div style={style.auditMeta}>
                          {item.ResolvedAt ? `Resolved ${formatDateLabel(item.ResolvedAt)}` : 'Reviewed'}
                          {item.ResolvedBy ? ` by ${item.ResolvedBy}` : ''}
                          {item.ResolutionNotes ? ` - ${item.ResolutionNotes}` : ''}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={(overview.reviewQueueColumns || fallbackOverview.reviewQueueColumns).length + 1}>
                    <div style={style.emptyState}>No review queue items for this filter.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderBillingFollowUps = () => {
    return (
      <div style={style.relationPanel}>
        <div style={style.relationHeader}>
          <div>
            <div style={style.relationTitle}>Billing follow-up queue</div>
            <div style={style.contextNote}>
              In-app only v1 surfacing for overdue invoices, renewals, missing season linkages, and draft follow-ups.
            </div>
          </div>
          <div style={style.inlineActions}>
            <select
              aria-label="Billing follow-up filter"
              style={style.toolbarSelect}
              value={followUpFilter}
              onChange={(e) => {
                setFollowUpFilter(e.target.value);
              }}
            >
              <option value="all">All follow-ups</option>
              <option value="overdueInvoice">Overdue invoices</option>
              <option value="renewalUpcoming">Renewal upcoming</option>
              <option value="missingSeasonLinkage">Missing season linkages</option>
              <option value="draftNotSent">Drafts not sent</option>
            </select>
          </div>
        </div>
        <div style={style.followUpSummaryGrid}>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Overdue invoices</div>
            <div style={style.statusValue}>{followUpCounts.overdueInvoice || 0}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Renewal upcoming</div>
            <div style={style.statusValue}>{followUpCounts.renewalUpcoming || 0}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Missing season linkages</div>
            <div style={style.statusValue}>{followUpCounts.missingSeasonLinkage || 0}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Draft not sent</div>
            <div style={style.statusValue}>{followUpCounts.draftNotSent || 0}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Open review queue items</div>
            <div style={style.statusValue}>{visibleReviewQueue.filter((item) => item.Status === 'open').length}</div>
          </div>
        </div>
        <div style={style.tableWrap}>
          <table style={style.table}>
            <thead>
              <tr>
                <th style={style.th}>Type</th>
                <th style={style.th}>Organization</th>
                <th style={style.th}>Department</th>
                <th style={style.th}>Subject</th>
                <th style={style.th}>Detail</th>
                <th style={style.th}>Status</th>
                {quickBooksUiEnabled ? <th style={style.th}>QuickBooks</th> : null}
                <th style={style.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {billingFollowUps.length > 0 ? (
                billingFollowUps.map((item) => (
                  <tr key={item.id}>
                    <td style={style.td}>{item.label}</td>
                    <td style={style.td}>{formatValue(item.organizationName)}</td>
                    <td style={style.td}>{formatValue(item.departmentName)}</td>
                    <td style={style.td}>{formatValue(item.subject)}</td>
                    <td style={style.td}>{formatValue(item.detail)}</td>
                    <td style={style.td}>{formatValue(item.status)}</td>
                    {quickBooksUiEnabled ? (
                      <td style={style.td}>
                        <div style={style.followUpLinks}>
                          {item.quickBooksUrl ? (
                            <button
                              type="button"
                              style={style.linkActionButton}
                              onClick={() => openQuickBooksUrl(item.quickBooksUrl)}
                            >
                              <FontAwesomeIcon icon={faExternalLinkAlt} />
                              View invoice
                            </button>
                          ) : (
                            <span style={style.subtleText}>No invoice deep link</span>
                          )}
                          {item.payNowUrl ? (
                            <button
                              type="button"
                              style={style.linkActionButton}
                              onClick={() => openQuickBooksUrl(item.payNowUrl)}
                            >
                              <FontAwesomeIcon icon={faExternalLinkAlt} />
                              Pay now
                            </button>
                          ) : (
                            <span style={style.subtleText}>
                              {item.payNowAvailable ? 'Pay now available' : 'Pay now unavailable in import-only v1'}
                            </span>
                          )}
                        </div>
                      </td>
                    ) : null}
                    <td style={style.td}>
                      <div style={style.inlineActions}>
                        {item.invoice ? (
                          <button type="button" style={style.actionButton} onClick={() => focusInvoice(item.invoice)}>
                            Focus invoice
                          </button>
                        ) : null}
                        {item.lineItem ? (
                          <button type="button" style={style.actionButton} onClick={() => focusLineItem(item.lineItem)}>
                            Focus line item
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8}>
                    <div style={style.emptyState}>No billing follow-up items for this filter.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderReportingPanel = () => {
    const summary = reporting.summary || fallbackOverview.reporting.summary;
    const revenueByInvoiceYearRows = (reporting.revenueByInvoiceYear || []).slice(0, 8);
    const revenueByPaymentYearRows = (reporting.revenueByPaymentYear || []).slice(0, 8);
    const revenueBySeasonYearRows = (reporting.revenueBySeasonYear || []).slice(0, 8);
    const customerRevenueMixRows = (reporting.customerRevenueMix || []).map((row) => ({
      ...row,
      CustomerType: row.customerType === 'new' ? 'New' : 'Returning',
    }));
    const taxExemptionSummaryRows = (reporting.taxExemptionStatusSummary || []).map((row) => ({
      ...row,
      StatusLabel: `${row.status || ''}`.replace(/_/g, ' '),
    }));

    return (
      <div style={style.relationPanel}>
        <div style={style.relationHeader}>
          <div>
            <div style={style.relationTitle}>Internal Reporting</div>
            <div style={style.contextNote}>
              Allocation-based MVP rollups for revenue, retention, LTV, prepaid coverage, and tax exemption exposure.
            </div>
          </div>
        </div>
        <div style={style.reportingSummaryGrid}>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Allocated Revenue</div>
            <div style={style.statusValue}>{formatMoney(summary.totalAllocatedRevenue)}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Paid Revenue</div>
            <div style={style.statusValue}>{formatMoney(summary.totalPaidRevenue)}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Outstanding Balance</div>
            <div style={style.statusValue}>{formatMoney(summary.totalOutstandingBalance)}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Prepaid Future Coverage</div>
            <div style={style.statusValue}>{formatMoney(summary.prepaidFutureCoverage)}</div>
          </div>
        </div>
        <div style={style.reportingGrid}>
          <div style={style.relationPanel}>
            <div style={style.relationHeader}>
              <div>
                <div style={style.relationTitle}>Revenue By Invoice Year</div>
                <div style={style.contextNote}>Separates invoice-year timing from season-year timing.</div>
              </div>
            </div>
            {renderTable({
              columns: [
                { key: 'invoiceYear', label: 'Invoice Year' },
                { key: 'allocatedRevenue', label: 'Allocated' },
                { key: 'paidRevenue', label: 'Paid' },
                { key: 'outstandingBalance', label: 'Outstanding' },
              ],
              rows: revenueByInvoiceYearRows,
              emptyText: 'No invoice-year rollups yet.',
            })}
          </div>
          <div style={style.relationPanel}>
            <div style={style.relationHeader}>
              <div>
                <div style={style.relationTitle}>Revenue By Payment Year</div>
                <div style={style.contextNote}>Shows when allocated value is actually realized through payment.</div>
              </div>
            </div>
            {renderTable({
              columns: [
                { key: 'paymentYear', label: 'Payment Year' },
                { key: 'allocatedRevenue', label: 'Allocated' },
                { key: 'paidRevenue', label: 'Paid' },
                { key: 'outstandingBalance', label: 'Outstanding' },
              ],
              rows: revenueByPaymentYearRows,
              emptyText: 'No payment-year rollups yet.',
            })}
          </div>
        </div>
        <div style={style.reportingGrid}>
          <div style={style.relationPanel}>
            <div style={style.relationHeader}>
              <div>
                <div style={style.relationTitle}>Revenue By Season Year</div>
                <div style={style.contextNote}>Season year stays distinct from invoice and payment timing.</div>
              </div>
            </div>
            {renderTable({
              columns: [
                { key: 'seasonYear', label: 'Season Year' },
                { key: 'allocatedRevenue', label: 'Allocated' },
                { key: 'paidRevenue', label: 'Paid' },
                { key: 'outstandingBalance', label: 'Outstanding' },
              ],
              rows: revenueBySeasonYearRows,
              emptyText: 'No season-year rollups yet.',
            })}
          </div>
          <div style={style.relationPanel}>
            <div style={style.relationHeader}>
              <div>
                <div style={style.relationTitle}>Revenue By Institution</div>
                <div style={style.contextNote}>Uses allocation coverage as the institution-level source of truth.</div>
              </div>
            </div>
            {renderTable({
              columns: [
                { key: 'OrganizationName', label: 'Institution' },
                { key: 'allocatedRevenue', label: 'Allocated' },
                { key: 'paidRevenue', label: 'Paid' },
                { key: 'outstandingBalance', label: 'Outstanding' },
              ],
              rows: reportingByOrganization.slice(0, 10),
              emptyText: 'No institution rollups available.',
            })}
          </div>
        </div>
        <div style={style.relationPanel}>
          <div style={style.relationHeader}>
            <div>
              <div style={style.relationTitle}>Revenue By Program</div>
              <div style={style.contextNote}>Program rollups are sourced from department-season allocations.</div>
            </div>
          </div>
          {renderTable({
            columns: [
              { key: 'DepartmentName', label: 'Program' },
              { key: 'OrganizationName', label: 'Institution' },
              { key: 'allocatedRevenue', label: 'Allocated' },
              { key: 'paidRevenue', label: 'Paid' },
            ],
            rows: reportingByDepartment.slice(0, 10),
            emptyText: 'No program rollups available.',
          })}
        </div>
        <div style={style.reportingGrid}>
          <div style={style.relationPanel}>
            <div style={style.relationHeader}>
              <div>
                <div style={style.relationTitle}>New Vs Returning Revenue</div>
                <div style={style.contextNote}>Revenue is tagged by whether the organization is in its first invoice year.</div>
              </div>
            </div>
            {renderTable({
              columns: [
                { key: 'invoiceYear', label: 'Invoice Year' },
                { key: 'CustomerType', label: 'Customer Type' },
                { key: 'allocatedRevenue', label: 'Allocated' },
                { key: 'paidRevenue', label: 'Paid' },
              ],
              rows: customerRevenueMixRows.slice(0, 12),
              emptyText: 'No customer mix rollups yet.',
            })}
          </div>
          <div style={style.relationPanel}>
            <div style={style.relationHeader}>
              <div>
                <div style={style.relationTitle}>Prepaid Future Season Coverage</div>
                <div style={style.contextNote}>Highlights prepayment allocations and future-season coverage.</div>
              </div>
            </div>
            {renderTable({
              columns: [
                { key: 'SeasonLabel', label: 'Season' },
                { key: 'DepartmentName', label: 'Program' },
                { key: 'invoiceYear', label: 'Invoice Year' },
                { key: 'seasonYear', label: 'Season Year' },
                { key: 'prepaidAllocationAmount', label: 'Prepaid Amount' },
              ],
              rows: reportingPrepaidCoverage.slice(0, 10),
              emptyText: 'No prepaid future coverage identified.',
            })}
          </div>
        </div>
        <div style={style.reportingGrid}>
          <div style={style.relationPanel}>
            <div style={style.relationHeader}>
              <div>
                <div style={style.relationTitle}>Institution LTV</div>
                <div style={style.contextNote}>Allocated invoiced and paid totals per institution.</div>
              </div>
            </div>
            {renderTable({
              columns: [
                { key: 'OrganizationName', label: 'Institution' },
                { key: 'allocatedRevenue', label: 'Allocated LTV' },
                { key: 'paidRevenue', label: 'Paid LTV' },
              ],
              rows: reportingLtvByOrganization.slice(0, 10),
              emptyText: 'No institution LTV values yet.',
            })}
          </div>
          <div style={style.relationPanel}>
            <div style={style.relationHeader}>
              <div>
                <div style={style.relationTitle}>Program LTV</div>
                <div style={style.contextNote}>Allocated invoiced and paid totals per program.</div>
              </div>
            </div>
            {renderTable({
              columns: [
                { key: 'DepartmentName', label: 'Program' },
                { key: 'OrganizationName', label: 'Institution' },
                { key: 'allocatedRevenue', label: 'Allocated LTV' },
                { key: 'paidRevenue', label: 'Paid LTV' },
              ],
              rows: reportingLtvByDepartment.slice(0, 10),
              emptyText: 'No program LTV values yet.',
            })}
          </div>
        </div>
        <div style={style.relationPanel}>
          <div style={style.relationHeader}>
            <div>
              <div style={style.relationTitle}>Tax Exemption Status Summary</div>
              <div style={style.contextNote}>Counts pending, expired, and missing coverage alongside reviewed states.</div>
            </div>
          </div>
          {renderTable({
            columns: [
              { key: 'StatusLabel', label: 'Status' },
              { key: 'count', label: 'Count' },
            ],
            rows: taxExemptionSummaryRows,
            emptyText: 'No tax exemption summary available.',
          })}
        </div>
      </div>
    );
  };

  const renderPaymentHistoryPanel = () => {
    if (!selectedInvoice) {
      return (
        <div style={style.relationPanel}>
          <div style={style.relationHeader}>
            <div style={style.relationTitle}>Payment history</div>
            <div style={style.contextNote}>Select an invoice to review payment status and imported events.</div>
          </div>
          <div style={style.emptyState}>Select an invoice to review payment details.</div>
        </div>
      );
    }

    return (
      <div style={style.relationPanel}>
        <div style={style.relationHeader}>
          <div>
            <div style={style.relationTitle}>{selectedInvoice.InvoiceNumber || 'Invoice'} payment history</div>
            <div style={style.contextNote}>Imported QuickBooks payments are read-only in v1.</div>
          </div>
        </div>
        <div style={style.paymentStatusGrid}>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Close-out Status</div>
            <div style={style.statusValue}>{selectedInvoiceCloseOutState.label}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Payment Status</div>
            <div style={style.statusValue}>{getDerivedPastDueLabel(selectedInvoice)}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Invoice Total</div>
            <div style={style.statusValue}>{formatMoney(selectedInvoice.TotalAmount)}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Balance Due</div>
            <div style={style.statusValue}>{formatMoney(selectedInvoice.BalanceDue)}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Due Date</div>
            <div style={style.statusValue}>{formatDateLabel(selectedInvoice.DueDate)}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Paid Date</div>
            <div style={style.statusValue}>{formatDateLabel(selectedInvoice.PaidDate)}</div>
          </div>
        </div>
        {selectedInvoiceCloseOutState.status === 'pending' ? (
          <div style={style.warningBanner}>
            QuickBooks void submitted on {formatDateLabel(selectedInvoice.VoidRequestedAt)}. Allocations are canceled locally while the next sync confirms the invoice is voided.
          </div>
        ) : null}
        {selectedInvoiceCloseOutState.status === 'failed' ? (
          <div style={style.warningBanner}>
            Close-out failed on {formatDateLabel(selectedInvoice.VoidFailedAt)}. {selectedInvoice.VoidFailureReason || 'Review the QuickBooks error and retry.'}
          </div>
        ) : null}
        <div style={style.paymentHelpText}>
          Refunds, credits, and adjustments remain manual in QuickBooks during v1. RezRATE only displays imported history here.
        </div>
        {selectedInvoicePayments.length > 0 ? (
          <div style={style.paymentEventList}>
            {selectedInvoicePayments.map((payment, index) => (
              <div key={payment.pk_QuickBooksPayment || `${payment.PaymentDate}-${index}`} style={style.paymentEventRow}>
                <div style={style.paymentEventDate}>{formatDateLabel(payment.PaymentDate || payment.updatedAt || payment.ImportedAt)}</div>
                <div style={style.paymentEventBody}>
                  <div style={style.paymentEventHeader}>
                    <span style={style.paymentEventType}>{getPaymentEventType(payment)}</span>
                    <span style={style.paymentEventAmount}>{formatMoney(payment.Amount)}</span>
                  </div>
                  <div style={style.paymentEventMeta}>
                    <span>{payment.PaymentMethod ? `Method: ${payment.PaymentMethod}` : 'Method unavailable'}</span>
                    <span>
                      {payment.ReferenceNumber ? `Reference: ${payment.ReferenceNumber}` : 'Reference unavailable'}
                    </span>
                    {Number(payment.UnappliedAmount) > 0 ? (
                      <span>Unapplied: {formatMoney(payment.UnappliedAmount)}</span>
                    ) : null}
                  </div>
                  {payment.Memo ? <div style={style.paymentEventNote}>{payment.Memo}</div> : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={style.emptyState}>No imported payment events are linked to this invoice yet.</div>
        )}
      </div>
    );
  };

  const renderTaxExemptionPanel = () => {
    const selectedDocumentStatus = selectedTaxExemptionDocument
      ? getTaxExemptionStatusLabel(selectedTaxExemptionDocument)
      : '--';

    return (
      <div style={style.relationPanel}>
        <div style={style.relationHeader}>
          <div>
            <div style={style.relationTitle}>Tax exemption documents</div>
            <div style={style.contextNote}>
              Admin review workspace for tax exemption submissions, expiration tracking, secure download access, and audit history.
            </div>
          </div>
        </div>
        <div style={style.toolbar}>
          <select
            aria-label="Tax exemption filter"
            style={style.toolbarSelect}
            value={taxExemptionFilter}
            onChange={(e) => {
              setTaxExemptionFilter(e.target.value);
            }}
          >
            <option value="all">All submissions</option>
            <option value="attention">Needs attention</option>
            <option value="submitted_pending">Pending review</option>
            <option value="needs_update">Needs update</option>
            <option value="approved">Approved</option>
            <option value="expired">Expired</option>
            <option value="rejected">Rejected</option>
          </select>
          <div style={style.subtleText}>
            {filteredTaxExemptionDocuments.length} visible document{filteredTaxExemptionDocuments.length === 1 ? '' : 's'}
          </div>
        </div>
        <div style={style.taxExemptionLayout}>
          <div style={style.relationPanel}>
            <div style={style.tableWrap}>
              <table style={style.table}>
                <thead>
                  <tr>
                    {(overview.taxExemptionColumns || fallbackOverview.taxExemptionColumns).map((column) => (
                      <th key={column.key} style={style.th}>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredTaxExemptionDocuments.length > 0 ? (
                    filteredTaxExemptionDocuments.map((document) => (
                      <tr
                        key={document.pk_TaxExemptionDocument}
                        style={
                          Number(selectedTaxExemptionDocument && selectedTaxExemptionDocument.pk_TaxExemptionDocument) ===
                          Number(document.pk_TaxExemptionDocument)
                            ? style.selectedTableRow
                            : undefined
                        }
                        onClick={() => selectTaxExemptionDocument(document)}
                      >
                        {(overview.taxExemptionColumns || fallbackOverview.taxExemptionColumns).map((column) => {
                          let cellValue = document[column.key];
                          if (column.key === 'ReviewStatus') {
                            cellValue = document.ReviewStatusDisplay;
                          }
                          if (['UploadedAt', 'EffectiveDate', 'ExpirationDate'].includes(column.key)) {
                            cellValue = formatDateLabel(cellValue);
                          }

                          return (
                            <td key={column.key} style={style.td}>
                              {formatCellValue(column.key, cellValue)}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={(overview.taxExemptionColumns || fallbackOverview.taxExemptionColumns).length}>
                        <div style={style.emptyState}>
                          {selectedOrganizationId
                            ? 'No tax exemption documents found for this organization.'
                            : 'No tax exemption documents imported yet.'}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div style={style.relationPanel}>
            <div style={style.relationHeader}>
              <div>
                <div style={style.relationTitle}>
                  {selectedTaxExemptionDocument ? selectedTaxExemptionDocument.DocumentName || 'Selected document' : 'Review details'}
                </div>
                <div style={style.contextNote}>
                  {selectedTaxExemptionDocument
                    ? `${selectedTaxExemptionDocument.OrganizationName || '--'} • ${selectedTaxExemptionDocument.ScopeLabel || 'Organization-wide'}`
                    : 'Select a tax exemption submission to review it, adjust dates, download it, and inspect history.'}
                </div>
              </div>
              {selectedTaxExemptionDocument ? (
                <div style={style.inlineActions}>
                  <button
                    type="button"
                    style={selectedTaxExemptionDocument.DocumentStorageKey ? style.actionButton : style.disabledActionButton}
                    disabled={!selectedTaxExemptionDocument.DocumentStorageKey || isTaxExemptionActionLoading}
                    onClick={() => downloadTaxExemptionDocument(selectedTaxExemptionDocument)}
                  >
                    Download
                  </button>
                </div>
              ) : null}
            </div>
            {selectedTaxExemptionDocument ? (
              <>
                <div style={style.statusGrid}>
                  <div style={style.statusCell}>
                    <div style={style.statusLabel}>Current Status</div>
                    <div style={style.statusValue}>{selectedDocumentStatus}</div>
                  </div>
                  <div style={style.statusCell}>
                    <div style={style.statusLabel}>Submitted By</div>
                    <div style={style.statusValue}>{formatValue(selectedTaxExemptionDocument.UploadedBy)}</div>
                  </div>
                  <div style={style.statusCell}>
                    <div style={style.statusLabel}>Submitted</div>
                    <div style={style.statusValue}>
                      {formatDateLabel(selectedTaxExemptionDocument.UploadedAt || selectedTaxExemptionDocument.createdAt)}
                    </div>
                  </div>
                  <div style={style.statusCell}>
                    <div style={style.statusLabel}>Effective</div>
                    <div style={style.statusValue}>{formatDateLabel(selectedTaxExemptionDocument.EffectiveDate)}</div>
                  </div>
                  <div style={style.statusCell}>
                    <div style={style.statusLabel}>Expires</div>
                    <div style={style.statusValue}>{formatDateLabel(selectedTaxExemptionDocument.ExpirationDate)}</div>
                  </div>
                </div>
                <div style={style.taxExemptionDetailGrid}>
                  <div style={style.formField}>
                    <label style={style.fieldLabel}>Review Action</label>
                    <select
                      style={style.toolbarSelect}
                      value={taxExemptionReviewDraft.ReviewStatus}
                      onChange={(e) =>
                        setTaxExemptionReviewDraft((prevState) => ({
                          ...prevState,
                          ReviewStatus: e.target.value,
                        }))
                      }
                    >
                      <option value="submitted_pending">Submitted / Pending Review</option>
                      <option value="approved">Approve</option>
                      <option value="rejected">Reject</option>
                      <option value="needs_update">Request Update</option>
                      <option value="expired">Mark Expired</option>
                      <option value="not_applicable">Not Applicable</option>
                    </select>
                  </div>
                  <div style={style.formFieldSmall}>
                    <label style={style.fieldLabel}>Effective Date</label>
                    <input
                      type="date"
                      style={style.toolbarInput}
                      value={taxExemptionReviewDraft.EffectiveDate}
                      onChange={(e) =>
                        setTaxExemptionReviewDraft((prevState) => ({
                          ...prevState,
                          EffectiveDate: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div style={style.formFieldSmall}>
                    <label style={style.fieldLabel}>Expiration Date</label>
                    <input
                      type="date"
                      style={style.toolbarInput}
                      value={taxExemptionReviewDraft.ExpirationDate}
                      onChange={(e) =>
                        setTaxExemptionReviewDraft((prevState) => ({
                          ...prevState,
                          ExpirationDate: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div style={style.formField}>
                    <label style={style.fieldLabel}>Internal Review Notes</label>
                    <textarea
                      style={{ ...style.toolbarInput, minHeight: 92, resize: 'vertical' }}
                      value={taxExemptionReviewDraft.ReviewNotes}
                      onChange={(e) =>
                        setTaxExemptionReviewDraft((prevState) => ({
                          ...prevState,
                          ReviewNotes: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div style={style.workflowActions}>
                  <button
                    type="button"
                    style={style.actionButton}
                    disabled={isTaxExemptionActionLoading}
                    onClick={() => runTaxExemptionReview('approved')}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    style={style.actionButton}
                    disabled={isTaxExemptionActionLoading}
                    onClick={() => runTaxExemptionReview('rejected')}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    style={style.actionButton}
                    disabled={isTaxExemptionActionLoading}
                    onClick={() => runTaxExemptionReview('needs_update')}
                  >
                    Request Update
                  </button>
                  <button
                    type="button"
                    style={style.actionButton}
                    disabled={isTaxExemptionActionLoading}
                    onClick={() => runTaxExemptionReview()}
                  >
                    Save Review
                  </button>
                </div>
                {taxExemptionActionMessage ? <div style={style.actionMessage}>{taxExemptionActionMessage}</div> : null}
                <div style={style.taxExemptionNoteGrid}>
                  <div style={style.taxExemptionNoteCard}>
                    <div style={style.fieldLabel}>Upload Notes</div>
                    <div style={style.taxExemptionNoteText}>
                      {formatValue(selectedTaxExemptionDocument.UploadNotes)}
                    </div>
                  </div>
                  <div style={style.taxExemptionNoteCard}>
                    <div style={style.fieldLabel}>Latest Review Notes</div>
                    <div style={style.taxExemptionNoteText}>
                      {formatValue(selectedTaxExemptionDocument.ReviewNotes)}
                    </div>
                  </div>
                </div>
                <div style={style.relationHeader}>
                  <div>
                    <div style={style.relationTitle}>Submission History</div>
                    <div style={style.contextNote}>Every upload, review decision, delete, and secure download is logged here.</div>
                  </div>
                </div>
                {taxExemptionAudit.length > 0 ? (
                  <div style={style.taxExemptionAuditList}>
                    {taxExemptionAudit.map((entry) => (
                      <div key={entry.pk_TaxExemptionDocumentAudit} style={style.taxExemptionAuditRow}>
                        <div style={style.taxExemptionAuditMeta}>
                          <strong>{formatValue(`${entry.Action || ''}`.replace(/_/g, ' '))}</strong>
                          <span>{formatDateLabel(entry.PerformedAt)}</span>
                          <span>{formatValue(entry.PerformedBy)}</span>
                        </div>
                        <div style={style.taxExemptionAuditMeta}>
                          <span>
                            {entry.PreviousReviewStatus ? `From: ${entry.PreviousReviewStatus}` : 'From: --'}
                          </span>
                          <span>{entry.NewReviewStatus ? `To: ${entry.NewReviewStatus}` : 'To: --'}</span>
                        </div>
                        {entry.Notes ? <div style={style.taxExemptionNoteText}>{entry.Notes}</div> : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={style.emptyState}>No audit history yet for this submission.</div>
                )}
              </>
            ) : (
              <div style={style.emptyState}>Select a tax exemption submission to begin review.</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderAllocationEditor = () => {
    const allocationLineItem = allocationEditor.lineItem;
    const availableQuantity = allocationLineItem && allocationLineItem.Quantity != null ? Number(allocationLineItem.Quantity) : null;
    const availableAmount = allocationLineItem && allocationLineItem.LineAmount != null ? Number(allocationLineItem.LineAmount) : null;

    if (!selectedLineItem) {
      return (
        <div style={style.relationPanel}>
          <div style={style.relationHeader}>
            <div style={style.relationTitle}>Line item allocation editor</div>
            <div style={style.contextNote}>Select an invoice line item to map it to departments and seasons.</div>
          </div>
          <div style={style.emptyState}>Select an invoice line item to begin allocating coverage.</div>
        </div>
      );
    }

    return (
      <div style={style.relationPanel}>
        <div style={style.relationHeader}>
          <div>
            <div style={style.relationTitle}>Line item allocation editor</div>
            <div style={style.contextNote}>
              {allocationLineItem ? `${formatValue(allocationLineItem.InvoiceNumber)} • ${formatValue(selectedLineItem.ProductSku)}` : 'Loading line item details'}
            </div>
          </div>
          <div style={style.inlineActions}>
            <button
              type="button"
              style={style.actionButton}
              disabled={isAllocationLoading || isAllocationSaving || !allocationLineItem || allocationLineItem.isAllocatable === false}
              onClick={addAllocationDraft}
            >
              <FontAwesomeIcon icon={faPlusCircle} />
              Add Allocation
            </button>
            <button
              type="button"
              style={style.actionButton}
              disabled={isAllocationLoading || isAllocationSaving || !allocationLineItem || allocationLineItem.isAllocatable === false}
              onClick={saveAllocations}
            >
              <FontAwesomeIcon icon={faLink} />
              Save Allocations
            </button>
          </div>
        </div>
        <div style={style.allocationSummaryGrid}>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Line Amount</div>
            <div style={style.statusValue}>{formatMoney(availableAmount)}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Allocated Amount</div>
            <div style={style.statusValue}>{formatMoney(editableAllocationTotals.amount)}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Remaining Amount</div>
            <div style={style.statusValue}>
              {availableAmount === null ? '--' : formatMoney(availableAmount - editableAllocationTotals.amount)}
            </div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Quantity</div>
            <div style={style.statusValue}>{availableQuantity === null ? '--' : availableQuantity}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Allocated Quantity</div>
            <div style={style.statusValue}>{editableAllocationTotals.quantity}</div>
          </div>
          <div style={style.statusCell}>
            <div style={style.statusLabel}>Remaining Quantity</div>
            <div style={style.statusValue}>
              {availableQuantity === null ? '--' : availableQuantity - editableAllocationTotals.quantity}
            </div>
          </div>
        </div>
        {allocationLineItem && allocationLineItem.isAllocatable === false ? (
          <div style={style.warningBanner}>
            This line item cannot be allocated yet. Make sure the QuickBooks customer is mapped and the invoice is active.
          </div>
        ) : null}
        {allocationMessage ? <div style={style.actionMessage}>{allocationMessage}</div> : null}
        {isAllocationLoading ? (
          <div style={style.emptyState}>Loading allocation details...</div>
        ) : editableAllocations.length > 0 ? (
          <div style={style.allocationEditorBody}>
            {editableAllocations.map((allocation, index) => {
              const seasonOptions = seasonOptionsByDepartment.get(Number(allocation.fk_Department)) || [];

              return (
                <div key={allocation.pk_QuickBooksInvoiceAllocation || `draft-${index}`} style={style.allocationRow}>
                  <div style={style.formField}>
                    <label style={style.fieldLabel}>Department</label>
                    <select
                      style={style.toolbarSelect}
                      value={allocation.fk_Department}
                      onChange={(e) => updateAllocationDraft(index, 'fk_Department', e.target.value)}
                    >
                      <option value="">Select department</option>
                      {allocationDepartments.map((department) => (
                        <option key={department.pk_Department} value={department.pk_Department}>
                          {department.DepartmentName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={style.formField}>
                    <label style={style.fieldLabel}>Season</label>
                    <select
                      style={style.toolbarSelect}
                      value={allocation.fk_Season}
                      onChange={(e) => updateAllocationDraft(index, 'fk_Season', e.target.value)}
                    >
                      <option value="">Select season</option>
                      {seasonOptions.map((season) => (
                        <option key={season.pk_Season} value={season.pk_Season}>
                          {season.SeasonLabel}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={style.formFieldSmall}>
                    <label style={style.fieldLabel}>Quantity</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      style={style.toolbarInput}
                      value={allocation.AllocationQuantity}
                      onChange={(e) => updateAllocationDraft(index, 'AllocationQuantity', e.target.value)}
                    />
                  </div>
                  <div style={style.formFieldSmall}>
                    <label style={style.fieldLabel}>Amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      style={style.toolbarInput}
                      value={allocation.AllocationAmount}
                      onChange={(e) => updateAllocationDraft(index, 'AllocationAmount', e.target.value)}
                    />
                  </div>
                  <div style={style.formFieldSmall}>
                    <label style={style.fieldLabel}>Type</label>
                    <select
                      style={style.toolbarSelect}
                      value={allocation.AllocationType || 'amount'}
                      onChange={(e) => updateAllocationDraft(index, 'AllocationType', e.target.value)}
                    >
                      <option value="amount">Standard</option>
                      <option value="courtesy">Courtesy</option>
                      <option value="prepayment">Prepayment</option>
                      <option value="comped">Comped</option>
                      <option value="discount">Discount</option>
                    </select>
                  </div>
                  <div style={style.formFieldSmall}>
                    <label style={style.fieldLabel}>Status</label>
                    <select
                      style={style.toolbarSelect}
                      value={allocation.CoverageStatus}
                      onChange={(e) => updateAllocationDraft(index, 'CoverageStatus', e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="reconciled">Reconciled</option>
                      <option value="mismatched">Mismatched</option>
                      <option value="voided">Voided</option>
                    </select>
                  </div>
                  <div style={style.formField}>
                    <label style={style.fieldLabel}>Notes</label>
                    <input
                      type="text"
                      style={style.toolbarInput}
                      value={allocation.Notes}
                      onChange={(e) => updateAllocationDraft(index, 'Notes', e.target.value)}
                    />
                  </div>
                  <div style={style.allocationActions}>
                    <button type="button" style={style.iconActionButton} onClick={() => markAllocationReconciled(index)}>
                      <FontAwesomeIcon icon={faCheckCircle} />
                    </button>
                    <button type="button" style={style.iconActionButton} onClick={() => removeAllocationDraft(index)}>
                      <FontAwesomeIcon icon={faTimesCircle} />
                    </button>
                    {allocation.pk_QuickBooksInvoiceAllocation ? (
                      <button
                        type="button"
                        aria-label="View allocation audit history"
                        title="View allocation audit history"
                        style={style.iconActionButton}
                        onClick={() => toggleAllocationAudit(allocation)}
                      >
                        <FontAwesomeIcon icon={faHistory} />
                      </button>
                    ) : null}
                  </div>
                  {allocationAuditFor === allocation.pk_QuickBooksInvoiceAllocation ? (
                    <div style={style.taxExemptionAuditList}>
                      {allocationAuditRows.length > 0 ? (
                        allocationAuditRows.map((entry) => (
                          <div key={entry.pk_QuickBooksInvoiceAllocationAudit} style={style.taxExemptionAuditRow}>
                            <div style={style.taxExemptionAuditMeta}>
                              <strong>{formatValue(`${entry.Action || ''}`.replace(/_/g, ' '))}</strong>
                              <span>{formatDateLabel(entry.PerformedAt)}</span>
                              <span>{formatValue(entry.PerformedBy)}</span>
                            </div>
                            <div style={style.taxExemptionAuditMeta}>
                              <span>
                                {entry.PreviousCoverageStatus ? `From: ${entry.PreviousCoverageStatus}` : 'From: --'}
                              </span>
                              <span>{entry.NewCoverageStatus ? `To: ${entry.NewCoverageStatus}` : 'To: --'}</span>
                            </div>
                            {entry.Notes ? <div style={style.taxExemptionNoteText}>{entry.Notes}</div> : null}
                          </div>
                        ))
                      ) : (
                        <div style={style.emptyState}>No audit history yet for this allocation.</div>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={style.emptyState}>No allocations yet. Add one or more department and season links for this line item.</div>
        )}
      </div>
    );
  };

  const renderAllocationWarnings = () => {
    const warnings = invoiceWorkspace.warnings || [];
    if (!selectedInvoice || !warnings.length) {
      return null;
    }

    return (
      <div style={style.warningStack}>
        {warnings.map((warning, index) => {
          const isError = warning.level === 'error';
          const detailRows = warning.lineItems || warning.seasons || warning.allocations || [];

          return (
            <div
              key={warning.code || `warning-${index}`}
              style={isError ? { ...style.warningCard, ...style.warningCardError } : style.warningCard}
            >
              <div style={style.warningHeader}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {warning.message || warning.code}
              </div>
              {warning.guidedAction ? <div style={style.warningBody}>{warning.guidedAction}</div> : null}
              {detailRows.length ? (
                <div style={style.warningMetaList}>
                  {detailRows.slice(0, 4).map((item, itemIndex) => (
                    <div key={`${warning.code || 'detail'}-${itemIndex}`}>
                      {item.SeasonName ||
                        item.Description ||
                        item.pk_QuickBooksInvoiceAllocation ||
                        item.pk_QuickBooksInvoiceLineItem}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const renderBillingContext = () => {
    const statusId = 'organizations-billing-sync-status';

    return (
      <div style={style.billingContext}>
        <div style={style.billingHeader}>
          <div>
            <div style={style.billingTitle}>Billing Context</div>
            <div style={style.contextNote}>
              Billing stays intentionally simple here: connect QuickBooks, map the organization, then manage imported invoices and allocations.
            </div>
          </div>
          <div style={style.statusPill} id={statusId}>
            <FontAwesomeIcon icon={faSyncAlt} spin={isLoadingFinancials} />
            {loadState === 'api' ? overview.sync.status : 'Schema Pending'}
          </div>
          <UncontrolledTooltip target={statusId}>Database ticket: {overview.databaseChangeTicket.key}</UncontrolledTooltip>
        </div>

        {renderQuickBooksStatusPanel()}
        {renderBillingOnboarding()}
        {billingLockedUntilMapped ? (
          <React.Fragment>
            <div style={style.mappingGateNote}>
              Map this organization to a QuickBooks customer first. The invoice list, review queue, payments, and
              reporting will appear after mapping is complete.
            </div>
            {renderCustomerMappings()}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {renderDisabledWorkflows()}
            {renderAllocationWarnings()}

            <div style={style.toolbar}>
              <FontAwesomeIcon icon={faSearch} />
              <input
                aria-label="Search invoices"
                style={style.toolbarInput}
                value={invoiceSearch}
                placeholder="Search invoices"
                onChange={(e) => {
                  setInvoiceSearch(e.target.value);
                }}
              />
              <select
                aria-label="Invoice filter"
                style={style.toolbarSelect}
                value={invoiceFilter}
                onChange={(e) => {
                  setInvoiceFilter(e.target.value);
                }}
              >
                <option value="all">All Invoices</option>
                <option value="open">Open/Overdue</option>
                <option value="overdue">Overdue</option>
                <option value="paid">Paid</option>
                <option value="needsReview">Needs Review</option>
              </select>
            </div>

            {renderBillingFollowUps()}
            {renderReportingPanel()}

            <div style={style.relationPanel}>
              <div style={style.relationHeader}>
                <div>
                  <div style={style.relationTitle}>
                    {selectedDepartment ? 'Department invoices' : selectedOrganization ? 'Organization invoices' : 'Invoices'}
                  </div>
                  <div style={style.contextNote}>Select an invoice to inspect line items and season coverage.</div>
                </div>
                <div style={style.draftActionGroup}>
                  {quickBooksUiEnabled ? (
                    <React.Fragment>
                      <div style={style.draftDateField}>
                        <label style={style.fieldLabel}>Draft Issue Date</label>
                        <input
                          type="date"
                          style={style.toolbarInput}
                          value={draftIssueDate}
                          disabled={!selectedInvoice || isQuickBooksActionLoading}
                          onChange={(e) => {
                            setDraftIssueDate(e.target.value);
                          }}
                        />
                      </div>
                      <div style={style.draftDateField}>
                        <label style={style.fieldLabel}>Draft Due Date</label>
                        <input
                          type="date"
                          style={style.toolbarInput}
                          value={draftDueDate}
                          disabled={!selectedInvoice || isQuickBooksActionLoading}
                          onChange={(e) => {
                            setDraftDueDate(e.target.value);
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        style={quickBooksActions.canCreateDraftInvoices ? style.actionButton : style.disabledActionButton}
                        disabled={!quickBooksActions.canCreateDraftInvoices || !selectedInvoice || isQuickBooksActionLoading}
                        onClick={runDraftInvoiceAction}
                      >
                        <FontAwesomeIcon icon={faFileInvoiceDollar} />
                        Create QuickBooks Draft
                      </button>
                      <div style={style.draftDateField}>
                        <label style={style.fieldLabel}>Close-out Reason</label>
                        <input
                          type="text"
                          style={style.toolbarInput}
                          value={closeOutReason}
                          placeholder="Customer declined renewal"
                          disabled={!selectedInvoice || isQuickBooksActionLoading}
                          onChange={(e) => {
                            setCloseOutReason(e.target.value);
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        style={canRunCloseOutAction ? style.actionButton : style.disabledActionButton}
                        disabled={!canRunCloseOutAction || isQuickBooksActionLoading}
                        onClick={runInvoiceCloseOutAction}
                      >
                        <FontAwesomeIcon icon={faBan} />
                        Close Out Unpaid Invoice
                      </button>
                    </React.Fragment>
                  ) : null}
                </div>
              </div>
              {quickBooksUiEnabled && selectedInvoice && closeOutDisabledReason ? (
                <div style={style.actionMessage}>{closeOutDisabledReason}</div>
              ) : null}
              {renderTable({
                columns: overview.invoiceColumns || fallbackOverview.invoiceColumns,
                rows: visibleInvoices,
                emptyText: selectedOrganization || selectedDepartment ? 'No invoices linked to this selection.' : 'No invoices synced.',
                onRowClick: setSelectedInvoice,
              })}
            </div>

            <div style={style.billingGrid}>
              <div style={style.relationPanel}>
                <div style={style.relationHeader}>
                  <div style={style.relationTitle}>Season to invoice line items</div>
                  <div style={style.contextNote}>
                    Select a department to show season coverage. A computed status can be manually overridden
                    (e.g. to resolve a "Needs Review" case) — every override is written to the audit trail.
                  </div>
                </div>
                {seasonOverrideMessage ? <div style={style.actionMessage}>{seasonOverrideMessage}</div> : null}
                {renderTable({
                  columns: overview.seasonCoverageColumns || fallbackOverview.seasonCoverageColumns,
                  rows: visibleSeasonCoverage,
                  emptyText: 'No season coverage linked to invoice line items yet.',
                  actionsLabel: 'Override',
                  renderActions: (season) => (
                    <div style={style.inlineActions}>
                      <button
                        type="button"
                        aria-label="Flag season for review"
                        title="Flag season for review"
                        style={style.iconActionButton}
                        disabled={isSeasonOverrideLoading || season.FinancialStatus === 'Needs Review'}
                        onClick={() => runSeasonStatusOverride(season, 'Needs Review')}
                      >
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                      </button>
                      <button
                        type="button"
                        aria-label="Override financial status"
                        title="Override financial status"
                        style={style.iconActionButton}
                        disabled={isSeasonOverrideLoading}
                        onClick={() => runSeasonStatusOverride(season)}
                      >
                        <FontAwesomeIcon icon={faExchangeAlt} />
                      </button>
                      <button
                        type="button"
                        aria-label="Clear status override"
                        title="Clear status override"
                        style={style.iconActionButton}
                        disabled={isSeasonOverrideLoading}
                        onClick={() => clearSeasonStatusOverride(season)}
                      >
                        <FontAwesomeIcon icon={faTimesCircle} />
                      </button>
                      <button
                        type="button"
                        aria-label="View status override history"
                        title="View status override history"
                        style={style.iconActionButton}
                        onClick={() => toggleSeasonOverrideHistory(season)}
                      >
                        <FontAwesomeIcon icon={faHistory} />
                      </button>
                    </div>
                  ),
                })}
                {seasonOverrideHistoryFor ? (
                  <div style={style.taxExemptionAuditList}>
                    {seasonOverrideHistoryRows.length > 0 ? (
                      seasonOverrideHistoryRows.map((entry) => (
                        <div key={entry.pk_SeasonStatusOverride} style={style.taxExemptionAuditRow}>
                          <div style={style.taxExemptionAuditMeta}>
                            <strong>{entry.ClearedAt ? 'Cleared' : 'Active'}</strong>
                            <span>{formatDateLabel(entry.OverriddenAt)}</span>
                            <span>{formatValue(entry.OverriddenBy)}</span>
                          </div>
                          <div style={style.taxExemptionAuditMeta}>
                            <span>Status: {formatValue(entry.OverrideStatus)}</span>
                            {entry.ClearedAt ? (
                              <span>
                                Cleared {formatDateLabel(entry.ClearedAt)} by {formatValue(entry.ClearedBy)}
                              </span>
                            ) : null}
                          </div>
                          {entry.Reason ? <div style={style.taxExemptionNoteText}>{entry.Reason}</div> : null}
                        </div>
                      ))
                    ) : (
                      <div style={style.emptyState}>No override history yet for this season.</div>
                    )}
                  </div>
                ) : null}
              </div>

              <div style={style.relationPanel}>
                <div style={style.relationHeader}>
                  <div>
                    <div style={style.relationTitle}>
                      {selectedInvoice ? `${selectedInvoice.InvoiceNumber || 'Invoice'} line items` : 'Invoice line items'}
                    </div>
                    <div style={style.contextNote}>
                      Line items belong to invoices. Select one to edit allocations. If QuickBooks imported no sales lines, create a pseudo line item first.
                    </div>
                  </div>
                  <div style={style.workspaceActions}>
                    <button
                      type="button"
                      style={style.actionButton}
                      disabled={
                        !selectedInvoice ||
                        isWorkspaceLoading ||
                        isWorkspaceActionLoading ||
                        !(invoiceWorkspace.guidance && invoiceWorkspace.guidance.canCreatePseudoLineItem)
                      }
                      onClick={ensurePseudoLineItem}
                    >
                      <FontAwesomeIcon icon={faPlusCircle} />
                      Create Pseudo Line Item
                    </button>
                  </div>
                </div>
                {renderTable({
                  columns: overview.lineItemColumns || fallbackOverview.lineItemColumns,
                  rows: selectedInvoiceLineItems,
                  emptyText: selectedInvoice ? 'No line items available yet for this invoice.' : 'Select an invoice to see its line items.',
                  onRowClick: loadLineItemAllocations,
                  getRowStyle: (row) =>
                    Number(selectedLineItem && selectedLineItem.pk_QuickBooksInvoiceLineItem) ===
                    Number(row.pk_QuickBooksInvoiceLineItem)
                      ? style.selectedTableRow
                      : undefined,
                })}
              </div>
            </div>

            {renderAllocationEditor()}

            <div style={style.relationPanel}>
              <div style={style.relationHeader}>
                <div style={style.relationTitle}>Line item to season coverage</div>
                <div style={style.contextNote}>Allocations bridge invoice line items to department seasons.</div>
              </div>
              {renderTable({
                columns: overview.lineItemSeasonLinkColumns || fallbackOverview.lineItemSeasonLinkColumns,
                rows: visibleLineItemSeasonLinks,
                emptyText: 'No invoice line item to season links for this selection.',
              })}
            </div>

            {renderPaymentHistoryPanel()}
            {renderTaxExemptionPanel()}
            {renderReviewQueue()}
          </React.Fragment>
        )}
      </div>
    );
  };

  return (
    <Row>
      <Col md={3}>
        <div style={EntityStyle.wrapper}>
          <OrganizationList
            setSelectedOrganization={(organization) => {
              setSelectedOrganization(organization);
              setSelectedDepartment(null);
              setSelectedInvoice(null);
            }}
            selectedOrganization={selectedOrganization}
            organizations={organizations}
            setOrganizations={setOrganizations}
            updateOrganizationArrayValues={updateOrganizationArrayValues}
          />
        </div>
      </Col>
      <Col md={3}>
        <div style={EntityStyle.wrapper}>
          <DepartmentList
            selectedOrganization={selectedOrganization}
            setSelectedDepartment={(department) => {
              setSelectedDepartment(department);
              setSelectedInvoice(null);
            }}
            organizations={organizations}
            departments={departments}
            setDepartments={setDepartments}
            selectedDepartment={selectedDepartment}
            onAddDepartment={() => {
              setSelectedDepartment(null);
              setSelectedInvoice(null);
              setIsCreatingDepartment(true);
            }}
          />
        </div>
      </Col>
      <Col md={6}>
        <div style={EntityStyle.headerWrapper}>{renderDepartmentName()}</div>
        <div style={style.detailScroll}>
          {renderOrganizationTabs()}
          {activeOrganizationTab === ORGANIZATION_TABS.billing && quickBooksUiEnabled
            ? renderBillingContext()
            : renderDepartmentWorkspace()}
        </div>
      </Col>
    </Row>
  );
};

export default FinancialIntelligencePanel;
